import type { PlacedPumpkin, Scene, LightKind, Stencil } from './types';
import { Renderer, Camera, PhotoLook, FILTERS } from './engine/renderer';
import { Governor, Tier, TIERS, TIER_LABEL, guessTier, readDevice, Device } from './engine/tier';
import { V3, v3, clamp } from './engine/math';
import { KINDS, VARIETIES, randomVarieties, sheetOf } from './pumpkin/shape';
import { DECK, DECK_X, WALL, DOOR_X, DOOR_W, DOOR_H, heightAt } from './scene/porch';
import { MAX_PUMPKINS, MAX_SCALE, MIN_SCALE, arrange, clear, settle, rest, Disc } from './scene/collide';
import { defaultScene, defaultLight, normaliseScene, CANDLE, LED } from './scene/defaults';
import { cleanScene } from './scene/clean';
import { PRESETS } from './stencil/presets';
import { ICONS, paintIcons } from './ui/icons';
import { ICONS as SICONS } from './stencil/icons';
import { StencilModel, handleFrame } from './stencil/model';
import { Sheet } from './paper/sheet';
import { Orbit } from './ui/orbit';
import { el, button, goo, gooDrips, colour, slider, toast, hexOf, ghost } from './ui/dom';
import { howTo, paperHowTo } from './ui/howto';
import * as api from './api';
import { drawCard } from './share/card';

type Step = 'choose' | 'stencil' | 'carve' | 'porch';
type Cutter = 'knife' | 'scraper' | 'fill';
type Hand = Cutter | 'spin';
/** the field of view everywhere but carving, where a long lens keeps the pumpkin from bulging */
const FOV = 0.95, CARVE_FOV = 0.5;
const Q = new URLSearchParams(location.search);
/** on the dev's own machine or a phone on the LAN: the engine's label and its slow-down notes show only here */
const LOCAL = /localhost$|^[\d.]+$/.test(location.hostname);
const $ = (id: string) => document.getElementById(id)!;

// ---- the engine, and which one this device gets

const canvas = $('gl') as HTMLCanvasElement;
const gl0 = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: true, stencil: false, powerPreference: 'high-performance' });
if (!gl0) {
  document.body.classList.add('nogl');
  $('nogl').hidden = false;
  throw new Error('no webgl2');
}
const device: Device = readDevice(gl0, Q.get('gpu'));
const guess = guessTier(device);
const forced = Q.get('tier') as Tier | null;
let tierPref: Tier | 'auto' = forced && TIERS.includes(forced) ? forced : (localStorage.getItem('pumpkin.tier') as Tier | 'auto' | null) ?? 'auto';
if (tierPref !== 'auto' && !TIERS.includes(tierPref)) tierPref = 'auto';
let tierWhy = tierPref === 'auto' ? guess.why : forced ? 'set in the link' : 'chosen';
const renderer = new Renderer(canvas, tierPref === 'auto' ? guess.tier : tierPref);
const gov = new Governor(1000 / 30, why => {
  const i = TIERS.indexOf(renderer.tier);
  if (i <= 0) return;
  renderer.setTier(TIERS[i - 1]);
  tierWhy = `dropped from ${TIER_LABEL[TIERS[i]]} at ${why}`;
  if (LOCAL) toast(`Running slow (${why}): switched to ${TIER_LABEL[renderer.tier]}.`);
  gov.reset(performance.now());
  refreshHud();
});

canvas.addEventListener('webglcontextlost', e => { e.preventDefault(); toast('The graphics card took a break. Hold on.'); });
canvas.addEventListener('webglcontextrestored', () => { renderer.restore(); dirty = true; gov.reset(performance.now()); });

// ---- state

let scene: Scene = defaultScene();
let sceneId: string | null = null;
let viewOnly = false;
let step: Step = 'choose';
/** the porch's selected pumpkin */
let sel = '';
/** the pumpkin going through the steps; an edit works on a copy so leaving part-way changes nothing */
let work: PlacedPumpkin | null = null;
let editing: { id: string; at: [number, number]; turn: number } | null = null;
let candidates: PlacedPumpkin[] = [];
let dirty = true;
const models = new Map<string, StencilModel>();

const uid = () => 'p' + Math.random().toString(36).slice(2, 9);
const portrait = () => innerWidth < innerHeight * 0.9;
const label = (p: PlacedPumpkin) => KINDS.find(k => k.kind === p.shape.kind)?.label ?? 'Pumpkin';
const blank = (): Stencil => ({ strokes: [], parts: [], mirror: true, cut: false, carve: [] });
const sizeOf = (p: PlacedPumpkin) => p.shape.size * (p.scale ?? 1);

const drafting = new Set<string>();
function modelOf(p: PlacedPumpkin): StencilModel {
  let m = models.get(p.id);
  if (m) return m;
  m = new StencilModel(p.stencil);
  m.rasterKey = p.id;
  const model = m, id = p.id;
  m.onChange(r => {
    const pk = work?.id === id ? work : scene.pumpkins.find(x => x.id === id);
    if (!pk) return;
    if (r === 'stencil') { drafting.delete(id); pk.stencil = model.toJSON(); renderer.restencil(id); if (pk !== work) saveDraft(); }
    // a drag can fire several moves a frame: one mask upload per frame is enough
    else if (r === 'draft' && !drafting.has(id)) {
      drafting.add(id);
      requestAnimationFrame(() => { if (drafting.delete(id)) renderer.restencil(id, model.preview()); });
    }
  });
  models.set(p.id, m);
  return m;
}

/** what the renderer shows: the gourds on the stage through the three steps, the porch after */
function shown(): Scene {
  if (step === 'porch') return scene;
  return { ...scene, pumpkins: step === 'choose' ? candidates : work ? [work] : [] };
}

let draftTimer = 0;
function saveDraft() {
  if (viewOnly) return;
  clearTimeout(draftTimer);
  draftTimer = window.setTimeout(() => {
    try { localStorage.setItem('pumpkin.draft', JSON.stringify({ scene, id: sceneId })); } catch { /* full: not fatal */ }
    noteUndo();
  }, 400);
}

// ---- undo on the porch: one step per settled change (the draft's pause after the last move), the camera left out
const undos: string[] = [];
let undoBase = '';
const porchState = () => JSON.stringify({ porch: scene.porch, pumpkins: scene.pumpkins });
function noteUndo() {
  // mid-drag or placing isn't settled, and their ends save again; a slide is waited out
  if (drag || placing) return;
  if (tweens.length) { setTimeout(noteUndo, 150); return; }
  const s = porchState();
  if (undoBase && s !== undoBase) { undos.push(undoBase); if (undos.length > 40) undos.shift(); }
  undoBase = s;
  showPorchTools();
}
function showPorchTools() {
  $('undo-b').hidden = !undos.length;
  $('tidy-b').hidden = !scene.pumpkins.length;
}
function undo() {
  if (step !== 'porch' || placing || drag || tweens.length || !undos.length) return;
  const s = JSON.parse(undos.pop()!) as Pick<Scene, 'porch' | 'pumpkins'>;
  if (!s.pumpkins.some(p => p.id === sel)) closePorchBits();
  // back past an Edit: the stencil model is rebuilt from the older carving
  const was = new Map(scene.pumpkins.map(p => [p.id, JSON.stringify(p.stencil)]));
  for (const p of s.pumpkins) if (was.get(p.id) !== JSON.stringify(p.stencil)) models.delete(p.id);
  scene.porch = s.porch;
  scene.pumpkins = s.pumpkins;
  undoBase = porchState();
  changed();
  showPorchTools();
}
/** every pumpkin to a sensible spot on the porch, sliding there; one undo step */
function tidy() {
  if (step !== 'porch' || placing || drag || tweens.length || !scene.pumpkins.length) return;
  const ps = scene.pumpkins, to = arrange(ps.map(radiusOf)), from = ps.map(p => [...p.at] as [number, number]);
  hidePop();
  tween(0.6, k => { ps.forEach((p, i) => { p.at = [lerp(from[i][0], to[i][0], ease(k)), lerp(from[i][1], to[i][1], ease(k))]; }); dirty = true; })
    .then(() => { ps.forEach((p, i) => { p.at = to[i]; }); changed(); });
}

function changed() { dirty = true; saveDraft(); addLabel(); }
function addLabel() { $('add-label').textContent = scene.pumpkins.length ? 'Carve another pumpkin' : 'Add a pumpkin'; }

// ---- small animations, run in the frame loop

const tweens: { t0: number; dur: number; fn: (k: number) => void; done: () => void }[] = [];
function tween(sec: number, fn: (k: number) => void): Promise<void> {
  return new Promise(done => tweens.push({ t0: performance.now(), dur: sec * 1000, fn, done }));
}
const ease = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
const easeIn = (k: number) => k * k * k;
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
function runTweens(now: number) {
  for (let i = tweens.length - 1; i >= 0; i--) {
    const t = tweens[i], k = Math.min(1, (now - t.t0) / t.dur);
    t.fn(k);
    dirty = true;
    if (k >= 1) { tweens.splice(i, 1); t.done(); }
  }
}
const wait = (ms: number) => new Promise(res => setTimeout(res, ms));

// ---- the camera

const orbit = new Orbit(canvas, renderer.camera);
/** the porch zoomed in on one pumpkin, while it is put in its place */
let placing = '';
/** the one being placed has been let go once: the ring to turn and size it comes up. The view stays close and the
 *  others stay out of the picture until OK */
let placeWide = false;
/** the house's colour buttons are out (Edit scene) */
/** the pumpkin the Edit panel has the camera close on */
let zoomed = '';
let zoomLens = 0;
/** the pumpkin turning slowly above the remove question */
let spinning: { id: string; turn: number; t0: number } | null = null;
/** the one being put in its place is alone in the picture; everything else is hidden until it is down */
function setPlacing(id: string) {
  placing = id;
  placeWide = false;
  renderer.hidden = new Set(id ? scene.pumpkins.filter(p => p.id !== id).map(p => p.id) : []);
  $('place').hidden = !id;
  if (id) document.body.dataset.placing = '1'; else delete document.body.dataset.placing;
  if (id && step === 'porch') frameStep();
  dirty = true;
}
/** the porch view faces the porch: it turns only so far either way, and backs off far enough to take in the house */
const PORCH_VIEW = { bounds: { min: 0.6, max: 10 }, pitch: { min: 0.02, max: 0.8 }, yaw: { min: -0.75, max: 0.75 } };
/** pull a camera into the porch's limits (an old save, or the start of the pan-in) */
function porchView(g: Camera) {
  g.yaw = clamp(g.yaw, PORCH_VIEW.yaw.min, PORCH_VIEW.yaw.max);
  g.pitch = clamp(g.pitch, PORCH_VIEW.pitch.min, PORCH_VIEW.pitch.max);
  g.dist = clamp(g.dist, PORCH_VIEW.bounds.min, PORCH_VIEW.bounds.max);
}
function frameStep() {
  const g = orbit.goal, tall = portrait();
  g.fov = viewOnly ? scene.camera?.fov ?? FOV : FOV;
  g.roll = viewOnly ? scene.camera?.roll ?? 0 : 0;
  const porch = step === 'porch';
  orbit.yaw = porch ? PORCH_VIEW.yaw : { min: -Infinity, max: Infinity };
  if (porch) {
    orbit.bounds = PORCH_VIEW.bounds;
    orbit.pitch = PORCH_VIEW.pitch;
    g.yaw = 0.18;
    orbit.unwind();
    const zid = placing || zoomed, p = scene.pumpkins.find(x => x.id === zid);
    // a pumpkin that has only just been added has no mesh yet: its middle is estimated from where it stands
    const c = !p ? null : renderer.centreOf(p.id) ?? [p.at[0], heightAt(p.at[0], p.at[1]) + sizeOf(p) * 0.8, p.at[1]] as V3;
    if (c && p && placing) { fitPlacing(true); return; }
    if (c && p) { g.pitch = 0.32; orbit.focus(c, sizeOf(p) * 6 + 0.55, zoomLens || (tall ? 0.45 : 0)); return; }
    g.pitch = 0.22;
    // the renderer keeps the horizontal view on a tall screen, so a phone needs no extra distance
    orbit.focus([0, 0.8, -1.2], 4.6);
    return;
  }
  orbit.bounds = { min: 0.35, max: 7 };
  orbit.pitch = step === 'carve' ? { min: -1.5, max: 1.5 } : { min: 0.02, max: 1.25 };
  g.yaw = 0;
  orbit.unwind();
  if (step === 'choose') { g.pitch = tall ? 0.34 : 0.26; orbit.focus([0, 0.2, tall ? 0.05 : 0], tall ? 0.95 : 1.35, tall ? 0.14 : 0); return; }
  const p = work, c = p && renderer.centreOf(p.id);
  const size = p ? sizeOf(p) : 0.18, y = c ? c[1] : size;
  if (step === 'stencil') {
    // big on the left, face on, the paper on the right; on a phone the gourd above the paper
    g.pitch = 0.12;
    if (tall) orbit.focus([0, y, 0], size * 4.6 + 0.3, 0.62);
    // off to the left by sliding the lens, not by aiming past it: at the edge of a wide view it stretched sideways
    // on a big monitor the layout keeps to a central column (app.css --col), so the gourd moves in with it
    else { const k = colShare(); g.fov = 0.6; orbit.focus([0, y, 0], size * 4.2 * 1.66 * (k < 1 ? 1.4 : 1), 0, (k < 1 ? -0.62 : -0.55) * k); }
    return;
  }
  // carving: a long lens from further back, so the pumpkin fills the screen without bulging
  g.pitch = 0.12;
  g.fov = CARVE_FOV;
  const t = Math.tan(CARVE_FOV / 2);
  // while a question is up: further back, the card below it
  const k = carveMode !== 'tools' ? 1.25 : 1, up = carveMode !== 'tools';
  if (tall) orbit.focus([0, y, 0], size * 1.0 * k / t, 0.24 * k);
  // with the tools out, the pumpkin sits a little right of centre, clear of the tools and their card on the left
  else orbit.focus([0, y, 0], size * 1.45 * k / t, up ? 0.2 : 0, up ? 0 : 0.12 * colShare());
}

/** the share of the width the central column takes: 1 below 1700x1200, then a 1800px column (app.css) */
const colShare = () => innerWidth >= 1700 && innerHeight >= 1200 ? Math.min(innerWidth, 1800) / innerWidth : 1;

// ---- step 1: choose your vegetable

let hovered = '';
const marks = $('marks');
const labels = el('div', 'labels');
marks.append(labels);

const GAP = 0.5;
const candidate = (shape: PlacedPumpkin['shape'], i: number): PlacedPumpkin => ({
  // on a tall phone a row of three comes out tiny: the middle one steps forward and the others close in behind, so the camera can come closer
  id: uid(), shape, stencil: blank(), light: defaultLight(), at: portrait() ? [(i - 1) * 0.3, i === 1 ? 0.28 : -0.22] : [(i - 1) * GAP, 0], turn: (i - 1) * -0.25, wall: 0.022,
});

function startChoose() {
  closePorchBits();
  sheet?.dispose(); sheet = null;
  renderer.paper = null;
  if (work && !editing) models.delete(work.id);
  candidates = randomVarieties(3).map(candidate);
  work = null;
  editing = null;
  go('choose');
  putLabels();
}

/** each gourd's name on a sticky label at a jaunty angle; it sticks on, and peels off when picked */
function putLabels() {
  labels.innerHTML = '';
  candidates.forEach((c, i) => {
    const b = button('', () => choose(c.id), 'gourd-label');
    const tag = el('span', 'sticker', label(c));
    tag.style.setProperty('--tilt', `${[-5, 3.5, -2.5][i % 3] + (Math.random() - 0.5) * 3}deg`);
    tag.style.animationDelay = `${0.15 + i * 0.12}s`;
    b.append(tag);
    b.dataset.id = c.id;
    labels.append(b);
  });
}

/** peel the labels off, and drop them once they are gone */
function peelLabels() {
  const all = [...labels.querySelectorAll<HTMLElement>('.gourd-label')];
  all.forEach((b, i) => { b.classList.add('peel'); b.style.setProperty('--pd', `${i * 0.07}s`); b.style.pointerEvents = 'none'; });
  setTimeout(() => all.forEach(b => b.remove()), 700);
}

/** Random: the die rolls, the gourds sink through the stage, three new ones come up and get their labels */
async function reroll() {
  if (step !== 'choose' || tweens.length) return;
  const die = $('rand-b');
  die.classList.remove('roll'); void die.offsetWidth; die.classList.add('roll');
  peelLabels();
  const old = candidates;
  await tween(0.45, k => old.forEach(o => renderer.lift.set(o.id, -easeIn(k) * 0.9)));
  old.forEach(o => renderer.lift.delete(o.id));
  candidates = randomVarieties(3).map(candidate);
  candidates.forEach(c => renderer.lift.set(c.id, -0.9));
  await tween(0.55, k => candidates.forEach(c => renderer.lift.set(c.id, -0.9 * (1 - ease(k)))));
  candidates.forEach(c => renderer.lift.delete(c.id));
  putLabels();
}

async function choose(id: string) {
  if (step !== 'choose' || tweens.length) return;
  const c = candidates.find(x => x.id === id);
  if (!c) return;
  peelLabels();
  const others = candidates.filter(x => x !== c);
  $('picker').hidden = true;
  const x0 = c.at[0], t0 = c.turn, side = 0;
  // the chosen one spins and slides aside; the other two drop through the stage
  await Promise.all([
    tween(1.1, k => { c.turn = t0 + ease(k) * (Math.PI * 2 - t0); c.at = [lerp(x0, side, ease(k)), 0]; }),
    tween(0.7, k => others.forEach(o => renderer.lift.set(o.id, -easeIn(k) * 0.8))),
  ]);
  others.forEach(o => renderer.lift.delete(o.id));
  c.turn = 0;
  // the paper starts with the classic face on it
  const classic = PRESETS.find(f => f.key === 'classic');
  if (classic) c.stencil = { ...blank(), ...JSON.parse(JSON.stringify(classic.stencil)), mirror: true, cut: false, carve: [] };
  candidates = [c];
  work = c;
  startStencil();
}

// the title: letters on an arch, each bobbing on its own beat
const arch = $('arch'), TITLE = 'Carve a pumpkin';
[...TITLE].forEach((ch, i, all) => {
  const t = (i - (all.length - 1) / 2) / ((all.length - 1) / 2);
  const s = el('span', 'arch-l' + (ch === ' ' ? ' sp' : ''), ch === ' ' ? '&nbsp;' : ch);
  s.setAttribute('aria-hidden', 'true');
  s.style.setProperty('--r', `${(t * 24).toFixed(1)}deg`);
  s.style.setProperty('--y', `${(t * t * 0.55).toFixed(3)}em`);
  s.style.setProperty('--bt', `${(0.9 + Math.random() * 0.7).toFixed(2)}s`);
  s.style.setProperty('--bd', `${(-Math.random() * 2).toFixed(2)}s`);
  arch.append(s);
});

// the die: white, purple spots, each face a 3x3 grid
{
  const PIPS = [[4], [0, 8], [0, 4, 8], [0, 2, 6, 8], [0, 2, 4, 6, 8], [0, 2, 3, 5, 6, 8]];
  $('die').innerHTML = PIPS.map((p, i) => `<span class="die-f f${i + 1}">${Array.from({ length: 9 }, (_, j) => `<i${p.includes(j) ? ' class="pip"' : ''}></i>`).join('')}</span>`).join('')
    // a solid core a hair inside the faces, so the rounded corners never show through to the back
    + ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'].map(c => `<span class="die-core ${c}"></span>`).join('');
}
$('rand-b').addEventListener('click', () => { void reroll(); });
const picker = $('picker');
// the grid's turning gourds are one picture; fetch it once the first screen has settled, so the grid opens with it
let atlas: HTMLImageElement | null = null;
const fetchAtlas = () => { if (!atlas) { atlas = new Image(); atlas.src = 'gourds.webp'; } };
setTimeout(fetchAtlas, 3000);
// or sooner, as soon as a finger or the mouse comes to the link
for (const ev of ['pointerenter', 'pointerdown']) $('pick-b').addEventListener(ev, fetchAtlas, { once: true });
$('pick-b').addEventListener('click', e => {
  e.stopPropagation();
  picker.hidden = !picker.hidden;
  if (picker.hidden) return;
  picker.innerHTML = '';
  picker.append(el('h2', 'picker-h', 'Pick a gourd'));
  const grid = el('div', 'picker-grid');
  for (const v of VARIETIES) {
    const b = button('', () => {
      if (step !== 'choose' || tweens.length) return;
      picker.hidden = true;
      // a fresh seed each time, so the same variety never comes out quite the same shape; it takes the middle spot
      const c = candidate(v.make((Math.random() * 1e9) | 0), 1);
      candidates[1] = c;
      renderer.sync(shown(), scene.pumpkins.map(p => p.id));
      putLabels();
      choose(c.id);
    }, 'pick-row');
    // the gourd turning: its row of the pre-rendered atlas, stepped through by CSS, each out of step with the next
    const spin = el('span', 'pick-spin');
    spin.style.setProperty('--row', String(VARIETIES.indexOf(v)));
    spin.style.animationDelay = `${(-Math.random() * 3).toFixed(2)}s`;
    b.append(spin, el('span', 'pick-name', v.label));
    grid.append(b);
  }
  picker.append(grid);
});
// the grid goes on a click anywhere outside it, or Escape
addEventListener('pointerdown', e => { const t = e.target as Node; if (!picker.hidden && !picker.contains(t) && !$('pick-b').contains(t)) picker.hidden = true; }, true);
addEventListener('keydown', e => { if (e.key === 'Escape' && !picker.hidden) picker.hidden = true; });
// Cmd/Ctrl+Z on the carving takes the last cut back (the paper has its own)
addEventListener('keydown', e => {
  if (step !== 'carve' || carveMode !== 'tools' || !work || cardOpen || !(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z' || e.shiftKey) return;
  if ((e.target as HTMLElement)?.closest?.('input[type=text], input[type=email], textarea')) return;
  e.preventDefault();
  modelOf(work).undo();
});

// ---- step 2: the stencil on paper

let sheet: Sheet | null = null;
const sheetHost = $('sheet-host');

function startStencil() {
  if (!work) return;
  sheet?.dispose();
  work.at = [0, 0];
  work.turn = 0;
  go('stencil');
  sheet = new Sheet(sheetHost, modelOf(work), { onCarve: () => toCarve() });
  // the first time, once the paper has flapped down: pen, ruler, eraser in three little films
  if (!localStorage.getItem('pumpkin.paper.toured')) setTimeout(() => {
    if (step === 'stencil' && !document.querySelector('.howto')) paperHowTo(() => localStorage.setItem('pumpkin.paper.toured', '1'));
  }, 1100);
}

/** the paper flies onto the gourd and wraps round it, then lets go and leaves the drawing traced on the skin */
let flying = false;
async function toCarve() {
  if (!work || !sheet || tweens.length || flying) return;
  flying = true;
  document.body.dataset.flying = '1';
  try { await flyOn(work, sheet); } finally { flying = false; delete document.body.dataset.flying; }
}

async function flyOn(p: PlacedPumpkin, sh: Sheet) {
  const paperEl = sh.root.querySelector<HTMLElement>('.sheet-paper')!;
  sh.leave();
  const x0 = p.at[0], t0 = p.turn;
  step = 'carve';
  document.body.dataset.step = 'carve';
  await tween(0.75, k => { p.at = [lerp(x0, 0, ease(k)), 0]; p.turn = lerp(t0, 0, ease(k)); });
  frameStep();
  await wait(350);
  // the sheet on screen shrinks onto the gourd's face; the paper in 3D takes over where it lands
  const c = renderer.screenOf(p.id), r = paperEl.getBoundingClientRect(), cr = canvas.getBoundingClientRect(), centre = renderer.centreOf(p.id), f = renderer.footOf(p.id);
  if (c && centre && f) {
    const edge = renderer.project(v3.add(centre, [f.r * 1.3, 0, 0]));
    const w = Math.abs(edge[0] - c[0]) * 2;
    paperEl.style.transition = 'transform 0.6s cubic-bezier(.5,0,.3,1)';
    paperEl.style.transformOrigin = '0 0';
    paperEl.style.transform = `translate(${cr.left + c[0] - w / 2 - r.left}px, ${cr.top + c[1] - w / 2 - r.top}px) scale(${w / r.width})`;
    await wait(600);
  }
  paperEl.style.visibility = 'hidden';
  renderer.paper = { id: p.id, canvas: sh.canvas, wrap: 0, alpha: 1 };
  await tween(1.1, k => { renderer.paper!.wrap = ease(k); });
  renderer.lit = true;
  await tween(0.6, k => { renderer.paper!.alpha = 1 - k; });
  renderer.paper = null;
  // an edit off the porch never asks how to carve: straight to the tools
  carveMode = editing ? 'tools' : 'ask';
  if (editing) showStencil = true;
  enterCarve();
  go('carve');
}

// ---- step 3: carve

let hand: Hand = 'knife';
let depth = 0.35;
let fillDepth = 1;
/** Show stencil: the stencil's outline dashed on the skin while carving with the tools. It only shows: cuts go anywhere */
let showStencil = false;
let carving = false;
/** arriving: the question; after cutting the holes: happy with it?; or the tools */
let carveMode: 'ask' | 'review' | 'tools' = 'ask';
const SIZES: Record<Cutter, number> = { knife: 0.022, scraper: 0.07, fill: 0.07 };
const SIZE_RANGE: Record<Cutter, [number, number]> = { knife: [0.01, 0.07], scraper: [0.03, 0.2], fill: [0.03, 0.2] };
const HANDS: [Hand, string, string][] = [['knife', 'Knife', 'knife'], ['fill', 'Fill', 'fill'], ['scraper', 'Scraper', 'scraper'], ['spin', 'Spin', 'spin']];
const isCutter = (h: Hand): h is Cutter => h === 'knife' || h === 'scraper' || h === 'fill';
const depthOf = (h: Cutter) => (h === 'scraper' ? depth : h === 'fill' ? fillDepth : 1);

/** the first scrape of a visit curls a peel off at the viewer */
let peeled = false, scrapeFrom: [number, number] | null = null;
function peel(x: number, y: number) {
  peeled = true;
  // a coil of rind, a ribbon wound round an axis: the near half of each turn shows the skin, the far half the flesh inside
  const P = (t: number) => { const a = t * 2.4 * Math.PI * 2, r = 32 * (1 - 0.4 * t); return [60 + Math.cos(a) * r, 22 + t * 76 + Math.sin(a) * r * 0.3, Math.sin(a)]; };
  let back = '', front = '';
  for (let i = 0; i < 120; i++) {
    const t = i / 120, [x0, y0, z0] = P(t), [x1, y1] = P((i + 1) / 120), h = 9 * (1 - 0.35 * t);
    const q = `M${x0.toFixed(1)} ${(y0 - h).toFixed(1)}L${x1.toFixed(1)} ${(y1 - h).toFixed(1)}L${x1.toFixed(1)} ${(y1 + h).toFixed(1)}L${x0.toFixed(1)} ${(y0 + h).toFixed(1)}Z`;
    if (z0 > 0) front += q; else back += q;
  }
  const curl = el('div', 'peel');
  curl.innerHTML = `<svg viewBox="0 0 120 120"><g class="pe-in"><path d="${back}"/></g><g class="pe-out"><path d="${front}"/></g></svg>`;
  curl.style.left = `${x}px`; curl.style.top = `${y}px`;
  curl.style.setProperty('--tx', `${innerWidth / 2 - x}px`);
  curl.style.setProperty('--ty', `${innerHeight * 0.62 - y}px`);
  document.body.append(curl);
  curl.addEventListener('animationend', () => curl.remove());
}
let cardOpen = false;
const cardHost = el('div', 'carve-cards');
$('carve-ui').after(cardHost);

/** where on the work's sheet the pointer is; carving reaches past the sheet, all the way round */
function sheetAt(e: PointerEvent, anywhere = false): [number, number] | null {
  if (!work) return null;
  const hit = renderer.pick(e.clientX, e.clientY);
  if (!hit || hit.id !== work.id) return null;
  const [x, y] = sheetOf(hit.hit.q, work.shape.squash);
  return !anywhere && (Math.abs(x) > 1 || Math.abs(y) > 1) ? null : [x, y];
}

function enterCarve() {
  hand = 'knife';
  if (work) modelOf(work).setTool('saw');
}

function setHandRaw(h: Hand) {
  if (work) modelOf(work).setTool('saw');
  hand = h;
}

function pickHand(h: Hand) {
  if (h === hand) return;
  setHandRaw(h);
  buildCarveUI();
}

/** a purple wobbly card over the pumpkin with a green main answer and a plain one */
function askCard(q: string, yes: string, no: string, onYes: () => void, onNo: () => void) {
  cardHost.innerHTML = '';
  const c = el('div', 'carve-card purple');
  const close = () => { cardHost.innerHTML = ''; cardOpen = false; };
  const row = el('div', 'ask-row');
  row.append(goo(yes, () => { close(); onYes(); }, 'ask-b'), button(no, () => { close(); onNo(); }, 'purp ask-b'));
  c.append(el('p', 'card-q', q), row);
  cardHost.append(c);
  cardOpen = true;
}

/** the box, corners and turn handle of the picked opening, drawn on the skin where it sits */
let selKey = '', selPx = 0;
let selPts: ([number, number] | null)[] = [];
let gourdSvg: SVGSVGElement | null = null;
function drawSel() {
  // on the carve screen it lives with the tools; on the paper's step it sits over the gourd on its own
  if (step !== 'carve' && !gourdSvg) {
    gourdSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    gourdSvg.setAttribute('class', 'sel-handles');
    gourdSvg.setAttribute('aria-hidden', 'true');
    canvas.after(gourdSvg);
  }
  if (step === 'carve' && gourdSvg?.firstChild) gourdSvg.innerHTML = '';
  const svg = step === 'carve' ? null : gourdSvg;
  if (!svg) return;
  const m = work && step === 'stencil' && sheet?.moving && !stowed ? modelOf(work) : null, h = m?.handles();
  if (!m || !work || !h) { if (svg.firstChild) { svg.innerHTML = ''; selKey = ''; } return; }
  const r = canvas.getBoundingClientRect();
  svg.setAttribute('viewBox', `0 0 ${r.width} ${r.height}`);
  const key = JSON.stringify(h) + work.id;
  const top = handleFrame(h).stalk;
  if (key !== selKey) {
    selKey = key;
    selPts = [h.nw, h.ne, h.se, h.sw, top, h.rotate].map(p => renderer.sheetToWorld(work!.id, p[0], p[1]) as V3 | null).map(w => w as unknown as [number, number] | null);
  }
  const s = selPts.map(w => (w ? renderer.project(w as unknown as V3) : null));
  if (s.some(p => !p)) { svg.innerHTML = ''; return; }
  const q = (p: [number, number] | null) => `${p![0].toFixed(1)},${p![1].toFixed(1)}`;
  // corner squares turn with the box, as it lies on screen
  const unit = (a: [number, number], b: [number, number], d: [number, number]) => { const l = Math.hypot(b[0] - a[0], b[1] - a[1]); return l > 1 ? [(b[0] - a[0]) / l, (b[1] - a[1]) / l] : d; };
  const ux = unit(s[0]!, s[1]!, [1, 0]), uy = unit(s[0]!, s[3]!, [0, 1]), c = 9;
  // pixels per sheet unit on the skin here, so a press within about 22px takes a handle
  const span = Math.hypot(h.ne[0] - h.nw[0], h.ne[1] - h.nw[1]);
  selPx = span > 1e-4 ? Math.hypot(s[1]![0] - s[0]![0], s[1]![1] - s[0]![1]) / span : 0;
  const sq = (p: [number, number] | null) => `<path class="hs" d="M${[[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([u, v]) => q([p![0] + (u * ux[0] + v * uy[0]) * c, p![1] + (u * ux[1] + v * uy[1]) * c])).join('L')}Z"/>`;
  svg.innerHTML = `<path class="hb" d="M${q(s[0])}L${q(s[1])}L${q(s[2])}L${q(s[3])}Z"/><path class="hb" d="M${q(s[4])}L${q(s[5])}"/>`
    + sq(s[0]) + sq(s[1]) + sq(s[2]) + sq(s[3]) + `<circle class="hs" cx="${s[5]![0]}" cy="${s[5]![1]}" r="12"/>`;
}

function range(label: string, k: string, min: number, max: number, get: () => number, set: (v: number) => void): HTMLElement {
  const row = el('label', 'tc');
  const input = el('input');
  Object.assign(input, { type: 'range', min: String(min), max: String(max), step: '0.005', value: String(get()) });
  input.dataset.k = k;
  input.addEventListener('input', () => set(+input.value));
  row.append(el('span', '', label), input);
  return row;
}

/** the chosen tool's own controls: size for all three, depth for the scraper and the fill; the knife always goes through */
function controls(h: Cutter): HTMLElement {
  const box = el('div', 'tool-ctl');
  box.dataset.for = h;
  box.append(range('Size', 'size', SIZE_RANGE[h][0], SIZE_RANGE[h][1], () => SIZES[h], v => { SIZES[h] = v; }));
  if (h === 'scraper') box.append(range('Depth', 'depth', 0.05, 0.95, () => depth, v => { depth = v; }));
  if (h === 'fill') box.append(range('Depth', 'depth', 0.05, 1, () => fillDepth, v => { fillDepth = v; }));
  return box;
}

/** the hand-made sign: chalk on a board, a chalk box that takes a tick: Show stencil */
function sign(): HTMLElement {
  const lines = el('label', 'lines');
  lines.dataset.hand = 'lines';
  const box = el('input');
  box.type = 'checkbox';
  box.checked = showStencil;
  box.addEventListener('change', () => { showStencil = box.checked; });
  lines.append(box, el('span', 'sign', `<span class="sign-t">Show<br>stencil</span>
<svg class="chalk" viewBox="0 0 64 64" aria-hidden="true"><path class="chalk-box" d="M8 10L55 8L57 54L10 56Z"/><path class="chalk-tick" d="M14 33L26 47L56 6"/></svg>`));
  return lines;
}

function buildCarveUI() {
  const ui = $('carve-ui');
  ui.innerHTML = '';
  if (step !== 'carve' || !work) return;
  const m = modelOf(work);
  if (carveMode === 'ask') {
    const card = el('div', 'carve-card ask purple');
    card.append(el('p', 'card-q', 'What would you like to do?'));
    const row = el('div', 'ask-row');
    row.append(
      goo('Carve it for me', () => { m.setCut(true); carveMode = 'review'; buildCarveUI(); frameStep(); }, 'ask-b'),
      button('I want to do it!', () => { carveMode = 'tools'; showStencil = true; startTour(); }, 'purp ask-b'),
    );
    card.append(row);
    ui.append(card);
    return;
  }
  if (carveMode === 'review') {
    const card = el('div', 'carve-card review purple');
    const row = el('div', 'ask-row');
    row.append(goo('Yes', () => finish(), 'ask-b'), button('No, I&rsquo;d like to edit', () => { carveMode = 'tools'; showStencil = true; startTour(); }, 'purp ask-b'));
    card.append(el('p', 'card-q', 'Happy with this?'), row);
    ui.append(card);
    return;
  }
  const tools = el('div', 'carve-tools');
  for (const [h, name, ico] of HANDS) {
    const b = button('', () => pickHand(h), 'hand piece' + (hand === h ? ' on' : ''));
    b.dataset.hand = h;
    b.setAttribute('aria-label', name);
    b.innerHTML = `${SICONS[ico] ?? ''}<span>${name}</span>`;
    if (hand === h) b.append(ghost());
    tools.append(b);
  }
  // Undo in words, beside Done
  const undo = button(`${ICONS.undo}<span>Undo</span>`, () => { if (work) modelOf(work).undo(); }, 'purp carve-undo');
  const bar = el('div', 'carve-bar');
  bar.append(undo, goo('Done', () => finish()));
  const col = el('div', 'sten-col');
  col.append(sign(), button(`${SICONS.pen}<span>Edit stencil</span>`, editStencil, 'purp sten-b'));
  ui.append(tools, col, bar);
  if (isCutter(hand)) ui.append(controls(hand));
}

/** into the tools; the first time, the three-film how-to comes up over them */
function startTour() {
  buildCarveUI();
  frameStep();
  if (!localStorage.getItem('pumpkin.toured')) howTo(() => localStorage.setItem('pumpkin.toured', '1'));
}

/** carving is over: out to the porch (a new one is shown there alone, zoomed in, "This is your pumpkin") */
function finish() {
  if (!work) return;
  done();
}

/** the carved pumpkin goes out on the porch: an edit back where it was, a new one front and centre to be put in place */
function done() {
  if (!work) return;
  const p = work;
  models.get(p.id)?.select(null);
  if (editing) {
    p.at = editing.at;
    p.turn = editing.turn;
    const i = scene.pumpkins.findIndex(x => x.id === editing!.id);
    if (i >= 0) scene.pumpkins[i] = p; else scene.pumpkins.push(p);
  } else {
    // a new one comes out at twice its carving size (Robin: the porch read them small); the next a little smaller,
    // so there is room for it among the others
    p.scale = 2;
    if (scene.pumpkins.length) {
      const r1 = sizeOf(p) / p.scale;
      p.scale = 1;
      for (const f of [1.6, 1.4, 1.2]) { if (findSpot(r1 * f * 1.1, [0.1, -0.5])) { p.scale = f; break; } }
    }
    // always in the middle of the deck to begin with, alone: if it is on another when OK is pressed, it slides to room
    p.at = [0, (WALL + 0) / 2];
    p.turn = (Math.random() - 0.5) * 0.3;
    scene.pumpkins.push(p);
  }
  const fresh = !editing;
  work = null;
  editing = null;
  candidates = [];
  go('porch');
  sel = p.id;
  setPlacing(fresh ? p.id : '');
  buildMenu();
  changed();
}

/**
 * Frame the pumpkin being placed with its ring, the knob and the turner all clear of the header and the This is your
 * pumpkin box: back off until they fit between them, then shift the lens so they sit in the middle of that band. Only
 * ever backs off (`first` aside, the opening close-up), and only once a drag or a resize is over: the view never moves
 * under a hand that is still on the pumpkin. After the first framing it drifts there slowly
 */
function fitPlacing(first = false) {
  const p = scene.pumpkins.find(x => x.id === placing);
  if (!p || step !== 'porch') return;
  const f = renderer.footOf(p.id), size = sizeOf(p), [x, z] = p.at;
  // heights from the mesh, but where it stands from the scene: a drop can slide it aside after the last frame drew it.
  // A pumpkin that has only just been added has no mesh yet, and its heights are estimated
  const y = f?.at[1] ?? heightAt(x, z), r = f?.r ?? size * 1.1;
  const foot: V3 = [x, y, z], top: V3 = [x, f?.top[1] ?? y + size * 1.7, z], c: V3 = [x, renderer.centreOf(p.id)?.[1] ?? y + size * 0.8, z];
  const pts: V3[] = [top, foot];
  for (let i = 0; i < 16; i++) { const a = (i / 16) * Math.PI * 2; pts.push(v3.add(foot, [Math.cos(a) * r * 1.3, 0.012, Math.sin(a) * r * 1.3])); }
  // the band: under the Back button, over the box, in from the sides
  const back = $('back-b').getBoundingClientRect(), box = $('place').getBoundingClientRect();
  const y0 = Math.max(16, back.bottom) + 12, y1 = (box.height ? box.top : innerHeight) - 16, x0 = 16, x1 = innerWidth - 16;
  const g: Camera = { ...orbit.goal, target: c, pitch: 0.32, yaw: first ? 0.18 : orbit.goal.yaw, lens: 0, lensX: 0 };
  const bbox = (d: number) => {
    g.dist = d;
    const q = renderer.projectWith(g, pts);
    if (q.some(v => !v)) return null;
    let a = Infinity, b = -Infinity, u = Infinity, w = -Infinity;
    for (const v of q as [number, number][]) { a = Math.min(a, v[0]); b = Math.max(b, v[0]); u = Math.min(u, v[1]); w = Math.max(w, v[1]); }
    // the knob sits off the ring's right (at least 46px out from the foot), the turner under its front: room for both
    const f0 = q[1]!;
    return { a: Math.min(a, f0[0] - 46) - 22, b: Math.max(b, f0[0] + 46) + 22, u: u - 8, w: Math.max(w, f0[1] + 40) + 22 };
  };
  const fits = (d: number) => { const k = bbox(d); return !!k && k.b - k.a <= x1 - x0 && k.w - k.u <= y1 - y0; };
  const max = PORCH_VIEW.bounds.max;
  let lo = first ? size * 3 + 0.28 : orbit.goal.dist, d = lo;
  if (!fits(lo)) {
    let hi = max;
    if (fits(hi)) {
      for (let i = 0; i < 18; i++) { const m = (lo + hi) / 2; if (fits(m)) hi = m; else lo = m; }
    }
    d = hi;
  }
  const k = bbox(d);
  // the lens moves the picture by NDC: the box's middle to the band's
  const lens = k ? ((k.u + k.w) / 2 - (y0 + y1) / 2) / innerHeight * 2 : 0;
  const lensX = k ? ((x0 + x1) / 2 - (k.a + k.b) / 2) / innerWidth * 2 : 0;
  const set = (q: Camera) => { q.target = c; q.pitch = 0.32; q.yaw = g.yaw; q.dist = Math.min(d, max); q.lens = lens; q.lensX = lensX; };
  if (!orbit.nudge(set)) set(orbit.goal);
  if (!first) orbit.glide = 1.8;
}
let fitAt = 0;
/** fit the view to the one being placed a moment after a pinch stops (a held knob waits for its lift) */
function fitSoon() {
  clearTimeout(fitAt);
  fitAt = window.setTimeout(() => { if (!knobDrag && !drag) fitPlacing(); }, 350);
}

function placed() {
  const p = scene.pumpkins.find(x => x.id === placing);
  const id = placing;
  setPlacing('');
  // put down and let go: its menu would otherwise float over the pumpkins behind it and take the next tap
  sel = '';
  if (p) {
    // it stays where it was put; any of the others it was set down on step aside to the nearest room
    const moved: [PlacedPumpkin, [number, number], [number, number]][] = [];
    for (const q of scene.pumpkins) {
      if (q.id === id) continue;
      const rq = radiusOf(q), discs = discsAround(q.id);
      if (clear(q.at, rq, discs)) continue;
      // sideways, so it never ends up standing in front of the new one
      const side = q.at[0] >= p.at[0] ? 1 : -1, want: [number, number] = [p.at[0] + side * (radiusOf(p) + rq + 0.05), q.at[1]];
      const to = settle(q.at, want, rq, discs, Renderer.onPorch);
      const dest = clear(to, rq, discs) ? to : freeSpot(rq, q.at, q.id);
      moved.push([q, [...q.at], dest]);
      q.at = dest;
    }
    if (moved.length) tween(0.5, k => { for (const [q, a, b] of moved) q.at = [lerp(a[0], b[0], ease(k)), lerp(a[1], b[1], ease(k))]; });
  }
  buildMenu();
  frameStep();
  changed();
}
gooDrips($('place-ok'));
$('place-ok').addEventListener('click', placed);
// back to the stage with the tools, before it is put down (as Back does while placing)
$('place-edit').addEventListener('click', () => { if (placing) recarve(placing); });

/** back to the porch from anywhere in the steps; an unfinished edit is dropped */
function toPorch() {
  if (editing) models.delete(editing.id);
  else if (work) models.delete(work.id);
  work = null;
  editing = null;
  candidates = [];
  go('porch');
}

// ---- the porch

const svgNS = 'http://www.w3.org/2000/svg';
const ringSvg = document.createElementNS(svgNS, 'svg');
ringSvg.classList.add('ring');
const ringPath = document.createElementNS(svgNS, 'path');
const ringHit = document.createElementNS(svgNS, 'path');
ringHit.classList.add('ring-hit');
ringSvg.append(ringPath, ringHit);
const knob = el('div', 'knob');
knob.title = 'Bigger or smaller';
knob.innerHTML = ICONS.scale;
const turner = el('div', 'turner');
turner.title = 'Turn it';
turner.innerHTML = ICONS.rotate;
const menu = el('div', 'pmenu');
const pop = el('div', 'pop');
pop.hidden = true;
marks.append(ringSvg, knob, turner, menu, pop);

function hidePop() {
  pop.hidden = true;
  if (zoomed) { zoomed = ''; zoomLens = 0; if (step === 'porch') frameStep(); }
}

$('scene-b').addEventListener('click', () => openScene());

// ---- the top bar, in the column's top left: Back (where it leads depends on the step), Quality, and on the porch Edit
// scene and Clear scene

/**
 * Back: the paper goes back to the gourds; carving goes back to the paper, or, editing one off the porch, to the porch
 * as it was; a new pumpkin being put in place goes back to its carving
 */
function back() {
  if (photo) { setPhoto(false); return; }
  if (tweens.length || flying) return;
  if (step === 'porch' && placing) { recarve(placing); return; }
  if (step === 'stencil') { if (editing) toPorch(); else startChoose(); return; }
  if (step !== 'carve' || !work) return;
  if (editing) { toPorch(); return; }
  editStencil();
}

/** carving back to the paper: the holes go back to being ink on paper; anything carved by hand stays */
function editStencil() {
  if (step !== 'carve' || !work || tweens.length || flying) return;
  const m = modelOf(work);
  m.select(null);
  if (m.stencil.cut) m.setCut(false);
  carveMode = 'ask';
  startStencil();
}
$('back-b').addEventListener('click', back);

const QUALITY: [Tier | 'auto', string][] = [['auto', 'Auto'], ['ultra', 'Ultra'], ['high', 'High'], ['mid', 'Medium'], ['low', 'Low']];
const qMenu = $('quality-m'), qBtn = $('quality-b');
function showQuality(open: boolean) {
  qMenu.hidden = !open;
  qBtn.setAttribute('aria-expanded', String(open));
  if (!open) return;
  qMenu.innerHTML = '';
  for (const [v, name] of QUALITY) {
    const on = v === tierPref;
    const b = button(v === 'auto' ? `${name} <small>(${TIER_LABEL[renderer.tier]})</small>` : name, () => { setTierPref(v); showQuality(false); }, 'q-item' + (on ? ' on' : ''));
    b.setAttribute('role', 'menuitemradio');
    b.setAttribute('aria-checked', String(on));
    b.dataset.q = v;
    qMenu.append(b);
  }
}
qBtn.addEventListener('click', e => { e.stopPropagation(); showQuality(qMenu.hidden === true); });
addEventListener('pointerdown', e => { if (!qMenu.hidden && !(e.target as Element).closest?.('.quality')) showQuality(false); }, true);
addEventListener('keydown', e => { if (e.key === 'Escape' && !qMenu.hidden) showQuality(false); });

$('clear-b').addEventListener('click', () => { closePorchBits(); ($('clear-dlg') as HTMLDialogElement).showModal(); });
gooDrips($('clear-yes'));
gooDrips($('scene-ok'));
$('undo-b').addEventListener('click', undo);
$('tidy-b').addEventListener('click', tidy);
addEventListener('keydown', e => {
  if (step !== 'porch' || viewOnly || !(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z' || e.shiftKey) return;
  if ((e.target as Element | null)?.closest('input, textarea, dialog[open]')) return;
  e.preventDefault();
  undo();
});
$('clear-yes').addEventListener('click', () => {
  ($('clear-dlg') as HTMLDialogElement).close();
  for (const p of scene.pumpkins) models.delete(p.id);
  scene.pumpkins = [];
  changed();
  startChoose();
});

function closePorchBits() {
  if (zoomed) { zoomed = ''; if (step === 'porch') frameStep(); }
  sel = '';
  menu.innerHTML = '';
  pop.hidden = true;
}

const LIGHTS: [LightKind, string][] = [['candle', 'Candle'], ['candles', 'Candles'], ['led', 'LED'], ['blink', 'Blinking']];

function buildMenu() {
  menu.innerHTML = '';
  const p = scene.pumpkins.find(x => x.id === sel);
  if (!p || viewOnly || placing) return;
  const row = el('div', 'pmenu-row');
  const kind = LIGHTS.find(l => l[0] === p.light.kind)?.[1] ?? 'Candle';
  row.append(
    button('Edit', () => edit(p.id)),
    button('Copy', () => copy(p.id)),
    button(`Light: ${kind}`, () => nextLight(p)),
    button('Remove', () => askRemove(p.id)),
  );
  menu.append(row);
}

/** the light goes round the four kinds; brightness and colour are the kind's own */
function nextLight(p: PlacedPumpkin) {
  const i = LIGHTS.findIndex(l => l[0] === p.light.kind), v = LIGHTS[(i + 1) % LIGHTS.length][0];
  p.light.kind = v;
  if (v === 'candle' || v === 'candles') p.light.color = [...CANDLE];
  else if (v === 'led') p.light.color = [...LED];
  else p.light.color = [1, 0.5, 0.16];
  p.light.brightness = 1;
  changed();
  buildMenu();
}

function selectPumpkin(id: string) {
  if (sel === id) return;
  sel = id;
  hidePop();
  buildMenu();
}

/** the radius a pumpkin takes up on the ground: the live mesh's when it has one */
const radiusOf = (p: PlacedPumpkin) => renderer.footOf(p.id)?.r ?? sizeOf(p) * 1.1;
const discsAround = (id: string): Disc[] => placing === id ? [] : scene.pumpkins.filter(q => q.id !== id).map(q => ({ at: q.at, r: radiusOf(q) }));

/** a spot for a pumpkin of radius `r`, clear of the rails and the others: the deck first, then the lawn */
function findSpot(r: number, near?: [number, number], skip = ''): [number, number] | null {
  const others: Disc[] = scene.pumpkins.filter(q => q.id !== skip).map(q => ({ at: q.at, r: radiusOf(q) }));
  const spots: [number, number][] = [];
  // the back of the deck first, by the wall where the light falls across the boards; never in the doorway
  for (const z of [-2.0, -1.55, -1.1, -0.6, -0.3]) for (let x = -DECK_X + 0.6; x <= DECK_X - 0.6; x += 0.2) {
    if (Math.abs(x - DOOR_X) < DOOR_W + 0.2 && z < -1.3) continue;
    spots.push([+x.toFixed(2), z]);
  }
  const lawn: [number, number][] = [];
  for (const z of [0.9, 1.5, 2.1, 2.7]) for (let x = -DECK_X + 0.3; x <= DECK_X - 0.3; x += 0.25) lawn.push([+x.toFixed(2), z]);
  const score = (s: [number, number]) => (near ? Math.hypot(s[0] - near[0], s[1] - near[1]) : Math.abs(s[0] - 0.4) * 0.5 + (s[1] + 2.0) * 1.2);
  spots.sort((a, b) => score(a) - score(b));
  lawn.sort((a, b) => score(a) - score(b));
  // room round it, and never straight behind another, where it would be hidden from the street
  const open = (s: [number, number], gap: number) => clear(s, r, others) && others.every(t => Math.hypot(t.at[0] - s[0], t.at[1] - s[1]) > t.r + r + gap);
  const hidden = (s: [number, number]) => others.some(t => t.at[1] > s[1] && Math.abs(t.at[0] - s[0]) < t.r + r);
  return spots.find(s => open(s, 0.1) && !hidden(s)) ?? spots.find(s => open(s, 0.02)) ?? lawn.find(s => open(s, 0.02)) ?? null;
}
const freeSpot = (r: number, near?: [number, number], skip = ''): [number, number] => findSpot(r, near, skip) ?? [0, 1.2];

function addPumpkin() {
  if (scene.pumpkins.length >= MAX_PUMPKINS) { toast('Fifteen is the most a porch will hold.'); return; }
  startChoose();
}

function edit(id: string) {
  const p = scene.pumpkins.find(x => x.id === id);
  if (!p) return;
  editing = { id, at: [...p.at] as [number, number], turn: p.turn };
  models.delete(id);
  work = { ...JSON.parse(JSON.stringify(p)), at: [0, 0], turn: 0 };
  candidates = [];
  closePorchBits();
  carveMode = 'tools';
  showStencil = true;
  enterCarve();
  go('carve');
}

/** the one just carved, taken off the porch before it was put down and back on the stage with the tools */
function recarve(id: string) {
  const i = scene.pumpkins.findIndex(x => x.id === id);
  if (i < 0) return;
  const [p] = scene.pumpkins.splice(i, 1);
  setPlacing('');
  sel = '';
  work = { ...p, at: [0, 0], turn: 0 };
  editing = null;
  candidates = [];
  closePorchBits();
  carveMode = 'tools';
  showStencil = true;
  enterCarve();
  go('carve');
  changed();
}

async function copy(id: string) {
  if (scene.pumpkins.length >= MAX_PUMPKINS) { toast('Fifteen is the most a porch will hold.'); return; }
  const p = scene.pumpkins.find(x => x.id === id);
  if (!p) return;
  const c: PlacedPumpkin = { ...JSON.parse(JSON.stringify(p)), id: uid() };
  c.at = freeSpot(radiusOf(p), p.at);
  c.turn = p.turn + (Math.random() - 0.5) * 0.6;
  scene.pumpkins.push(c);
  changed();
  selectPumpkin(c.id);
  // pops up beside it and bounces a couple of times
  await tween(0.8, k => renderer.lift.set(c.id, Math.abs(Math.sin(k * Math.PI * 2.5)) * 0.22 * Math.pow(1 - k, 1.6)));
  renderer.lift.delete(c.id);
}

let removing = '';
/** the porch goes quiet behind the question: just this pumpkin, close, turning slowly, in the top half */
function askRemove(id: string) {
  const p = scene.pumpkins.find(x => x.id === id);
  if (!p) return;
  removing = id;
  hidePop();
  zoomed = id;
  zoomLens = 0.5;
  renderer.hidden = new Set(scene.pumpkins.filter(q => q.id !== id).map(q => q.id));
  spinning = { id, turn: p.turn, t0: performance.now() };
  document.body.dataset.asking = '1';
  frameStep();
  buildMenu();
  ($('remove-dlg') as HTMLDialogElement).showModal();
}
$('remove-dlg').addEventListener('close', () => {
  const sp = spinning;
  spinning = null;
  delete document.body.dataset.asking;
  const p = sp && scene.pumpkins.find(x => x.id === sp.id);
  if (p && sp) p.turn = sp.turn;
  renderer.hidden = new Set();
  zoomLens = 0;
  zoomed = '';
  if (step === 'porch') frameStep();
  buildMenu();
  dirty = true;
});
gooDrips($('remove-yes'));
gooDrips($('own'));
gooDrips($('share-go'));
$('remove-yes').addEventListener('click', () => { ($('remove-dlg') as HTMLDialogElement).close(); removePumpkin(removing); });
document.querySelectorAll<HTMLElement>('.dlg-x').forEach(b => b.addEventListener('click', () => b.closest('dialog')?.close()));

// Found a bug?'s form: mailed to Robin with the page and the device
gooDrips($('bug-go'));
const bugDlg = $('bug-dlg') as HTMLDialogElement, bugF = $('bug-f') as HTMLFormElement, bugDone = $('bug-done');
$('bugs-b').addEventListener('click', () => { bugF.reset(); bugDone.hidden = true; ($('bug-go') as HTMLButtonElement).disabled = false; bugDlg.showModal(); });
bugF.addEventListener('submit', async e => {
  e.preventDefault();
  const go = $('bug-go') as HTMLButtonElement;
  go.disabled = true;
  try {
    await api.reportBug({
      message: ($('bug-msg') as HTMLTextAreaElement).value, email: ($('bug-mail') as HTMLInputElement).value.trim(), website: ($('bug-web') as HTMLInputElement).value,
      page: `${location.pathname}${location.search} (${step}${viewOnly ? ', viewing' : ''})`, info: `${innerWidth}x${innerHeight} @${devicePixelRatio}, ${TIER_LABEL[renderer.tier]}`,
    });
    bugDone.textContent = 'Sent. Thank you.';
    bugDone.hidden = false;
    setTimeout(() => bugDlg.close(), 1400);
  } catch (err) {
    bugDone.textContent = `That didn't send: ${(err as Error).message}`;
    bugDone.hidden = false;
    go.disabled = false;
  }
});

function removePumpkin(id: string) {
  const i = scene.pumpkins.findIndex(p => p.id === id);
  if (i < 0) return;
  scene.pumpkins.splice(i, 1);
  models.delete(id);
  closePorchBits();
  changed();
}

// ---- the pumpkin's own looks, live: variety, colour, flaws, stalk. Out of the porch menu since round 7 (its Edit carves
// again); POTENTIAL: could come back under Edit scene

const SKINS: RGB[] = [[0.8, 0.2, 0.02], [0.9, 0.38, 0.03], [0.85, 0.62, 0.08], [0.75, 0.72, 0.6], [0.5, 0.58, 0.55], [0.2, 0.32, 0.4], [0.5, 0.05, 0.03], [0.1, 0.35, 0.05], [0.3, 0.08, 0.5], [0.04, 0.04, 0.05]];

function openEdit(id: string) {
  const p = scene.pumpkins.find(x => x.id === id);
  if (!p) return;
  pop.innerHTML = '';
  pop.dataset.part = 'edit';
  pop.append(el('h3', 'pop-h', 'This pumpkin'));
  const live = () => changed();
  const cur = p.shape.variety ?? VARIETIES.find(v => v.make(1).kind === p.shape.kind)?.key ?? VARIETIES[0].key;
  const sel = el('select', 'pop-sel');
  for (const v of VARIETIES) { const o = el('option', '', v.label); o.value = v.key; sel.append(o); }
  sel.value = cur;
  sel.addEventListener('change', () => {
    const v = VARIETIES.find(x => x.key === sel.value);
    if (!v) return;
    // a new variety keeps this one's seed, size on the porch, sculpting, flaws and stalk
    const n = v.make(p.shape.seed), keep = p.shape;
    p.shape = { ...n, sculpt: keep.sculpt, flaws: keep.flaws, stem: { ...n.stem, wild: keep.stem.wild } };
    live();
  });
  const lab = (t: string) => el('div', 'pop-l', t);
  const row = el('div', 'swatches round');
  for (const c of SKINS) {
    const b = button('', () => { p.shape.skin = [...c]; live(); row.querySelectorAll('.sw').forEach(x => x.classList.toggle('on', x === b)); }, 'sw' + (p.shape.skin.join() === c.join() ? ' on' : ''));
    b.style.background = hexOf(c);
    b.setAttribute('aria-label', 'Skin ' + hexOf(c));
    row.append(b);
  }
  const flaws = slider('Imperfections', 0, 1, 0.01, () => p.shape.flaws ?? 0, v => { p.shape.flaws = v; live(); }, () => '');
  const stalk = slider('Stalk', 0, 1, 0.01, () => p.shape.stem.wild ?? 0, v => { p.shape.stem.wild = v; live(); }, () => '');
  const sub = (a: string, b: string) => { const r = el('div', 'pop-ends'); r.append(el('span', '', a), el('span', '', b)); return r; };
  const again = button('Carve again', () => { pop.hidden = true; edit(id); }, 'goo pop-again');
  pop.append(lab('Variety'), sel, lab('Colour'), row, flaws.root, sub('as grown', 'a mess'), stalk.root, sub('neat', 'crazy'), again);
  gooDrips(again);
  pop.hidden = false;
  zoomed = id;
  frameStep();
  buildMenu();
  placePop(id);
}

/** beside the pumpkin on a desk, along the bottom on a phone */
function placePop(_id: string) {
  const w = pop.offsetWidth || 250, h = pop.offsetHeight || 300;
  if (portrait()) { pop.style.left = `${Math.max(8, (innerWidth - w) / 2)}px`; pop.style.top = `${Math.max(60, innerHeight - h - 12)}px`; return; }
  pop.style.left = `${innerWidth - w - 28}px`;
  pop.style.top = `${clamp((innerHeight - h) / 2, 60, innerHeight - h - 12)}px`;
}

// ---- the house: Edit scene opens its colours, the door, the siding and the boards together

type Part = 'door' | 'siding' | 'planks';
type RGB = [number, number, number];
const SWATCH: Record<Part, RGB[]> = {
  door: [[0.32, 0.03, 0.02], [0.02, 0.02, 0.02], [0.02, 0.1, 0.05], [0.02, 0.04, 0.12], [0.45, 0.25, 0.02], [0.55, 0.5, 0.42],
    [0.28, 0.03, 0.5], [0.05, 0.55, 0.04], [0.85, 0.28, 0.01], [0.7, 0.03, 0.25], [0.02, 0.45, 0.5]],
  siding: [[0.07, 0.1, 0.09], [0.03, 0.03, 0.04], [0.2, 0.06, 0.04], [0.25, 0.22, 0.18], [0.06, 0.08, 0.14], [0.3, 0.28, 0.24],
    [0.16, 0.04, 0.3], [0.08, 0.32, 0.05], [0.5, 0.15, 0.02], [0.4, 0.04, 0.2], [0.04, 0.28, 0.3]],
  planks: [[0.2, 0.15, 0.1], [0.12, 0.08, 0.05], [0.3, 0.25, 0.18], [0.08, 0.08, 0.08], [0.18, 0.06, 0.04],
    [0.2, 0.05, 0.35], [0.1, 0.4, 0.06], [0.55, 0.2, 0.02], [0.05, 0.2, 0.3]],
};
const PART_LABEL: Record<Part, string> = { door: 'Door', siding: 'Siding', planks: 'Boards' };

/** Edit scene: each part of the house with its swatches and a free colour, taken as they're chosen */
function openScene() {
  closePorchBits();
  const po = scene.porch, box = $('scene-parts');
  box.innerHTML = '';
  for (const part of ['door', 'siding', 'planks'] as Part[]) {
    const div = el('div', 'scene-part'), row = el('div', 'swatches');
    div.dataset.part = part;
    const mark = () => row.querySelectorAll('.sw').forEach((b, i) => b.classList.toggle('on', po[part].join() === SWATCH[part][i].join()));
    const any = colour('Any colour', () => po[part], c => { po[part] = c; mark(); changed(); });
    for (const c of SWATCH[part]) {
      const b = button('', () => { po[part] = [...c]; mark(); any.refresh(); changed(); }, 'sw');
      b.style.background = hexOf(c);
      b.setAttribute('aria-label', `${PART_LABEL[part]} ${hexOf(c)}`);
      row.append(b);
    }
    mark();
    const top = el('div', 'scene-top');
    top.append(el('h3', 'scene-name', PART_LABEL[part]), any.root);
    div.append(top, row);
    box.append(div);
  }
  ($('scene-dlg') as HTMLDialogElement).showModal();
}

// ---- the engine: auto, or ?tier= in the link

function setTierPref(v: Tier | 'auto') {
  tierPref = v;
  localStorage.setItem('pumpkin.tier', v);
  const t = v === 'auto' ? guess.tier : v;
  tierWhy = v === 'auto' ? guess.why : 'chosen';
  if (t !== renderer.tier) { renderer.setTier(t); dirty = true; }
  gov.reset(performance.now());
  refreshHud();
}

$('add').addEventListener('click', () => addPumpkin());
// the sign's bulbs, round its edge
{
  const at: [number, number][] = [];
  for (let i = 0; i <= 8; i++) at.push([i / 8, 0], [i / 8, 1]);
  for (const y of [1 / 3, 2 / 3]) at.push([0, y], [1, y]);
  $('add').querySelector('.leds')!.innerHTML = at.map(([x, y], i) => `<i style="left:${x * 100}%;top:${y * 100}%;--i:${i}"></i>`).join('');
}
/** where the sign hangs: the top rail of the right-hand railing, part way along */
const SIGN_AT: V3 = [2.25, DECK + 0.97, -0.05];


// ---- the pointer on the 3D view

// iOS Safari ignores the viewport's maximum-scale for a pinch on the page; the 3D view has its own pinch
for (const ev of ['gesturestart', 'gesturechange']) document.addEventListener(ev, e => e.preventDefault(), { passive: false });
// and a quick second tap zooms on iOS whatever touch-action says: cancel it (the paper and the 3D view time their own
// double-taps from pointer events), handing a button its click back
let lastTouch = 0;
document.addEventListener('touchend', e => {
  const now = e.timeStamp;
  if (now - lastTouch < 350 && e.cancelable) {
    e.preventDefault();
    (e.target as Element | null)?.closest<HTMLElement>('button, a, label, [role=switch]')?.click();
  }
  lastTouch = now;
}, { passive: false });
// if the page got zoomed anyway, pointers no longer land where they look: put the scale back
const vp = document.querySelector<HTMLMetaElement>('meta[name=viewport]');
visualViewport?.addEventListener('resize', () => {
  if (!vp || visualViewport!.scale < 1.01 || document.activeElement?.matches('input[type=text], input[type=email], textarea')) return;
  const c = vp.content;
  vp.content = c.replace(/,?\s*maximum-scale=[\d.]+/, '') + ', maximum-scale=1.0';
  requestAnimationFrame(() => { vp.content = c; });
});
let gourdDraw = false;
let stowed = '';
let drag: { id: string; y: number; off: [number, number]; from: [number, number]; x0: number; y0: number; moved: boolean; cx: number; cy: number } | null = null;
/** the dragged pumpkin under the pointer; again each frame while the view drifts, so it stays under the finger */
function dragTo() {
  if (!drag) return;
  const p = scene.pumpkins.find(x => x.id === drag!.id);
  // through the others: only the railings stop it; it finds room when let go (`settleDrop`)
  if (p) { p.at = settle(p.at, renderer.dragOnPorch(drag.cx, drag.cy, drag.y, drag.off), radiusOf(p), [], Renderer.onPorch); dirty = true; }
}

/** let go on top of another: it slides off to the nearest room, back the way it came first */
function settleDrop(id: string, from: [number, number]) {
  const p = scene.pumpkins.find(x => x.id === id);
  if (!p) return;
  const a: [number, number] = [...p.at], b = rest(a, radiusOf(p), discsAround(id), Renderer.onPorch, from);
  if (b[0] === a[0] && b[1] === a[1]) return;
  let mine = p.at = b;
  // anything else that moves it (a new drag, a load) takes over from the slide
  tween(0.22, k => { if (p.at === mine) mine = p.at = [lerp(a[0], b[0], ease(k)), lerp(a[1], b[1], ease(k))]; });
}

orbit.tool = {
  begin(e) {
    if (viewOnly) return false;
    if (step === 'choose') {
      const hit = renderer.pick(e.clientX, e.clientY);
      if (hit) choose(hit.id);
      return true;
    }
    if (step === 'stencil' && sheet && !tweens.length && !flying) {
      const at = sheetAt(e);
      if (at) gourdDraw = sheet.press(at, e.pointerType === 'pen' ? e.pressure : 0.5, 2);
      return true;
    }
    if (step === 'stencil' || tweens.length || flying) return true;
    if (step === 'carve' && work) {
      if (carveMode !== 'tools' || hand === 'spin') return false;
      if (cardOpen) return true;
      const at = sheetAt(e, true), m = modelOf(work);
      if (!at) return true;
      m.carveDown(at[0], at[1], { tool: hand as Cutter, depth: depthOf(hand as Cutter), width: SIZES[hand as Cutter] });
      carving = true;
      scrapeFrom = hand === 'scraper' && !peeled ? [e.clientX, e.clientY] : null;
      return true;
    }
    if (step === 'porch') {
      if (photo) return false;
      const hit = renderer.pick(e.clientX, e.clientY);
      if (!hit || (placing && hit.id !== placing)) return false;
      selectPumpkin(hit.id);
      // by its middle, but always well under the eye: a plane near or above it turns the drag round or throws it off
      const p = scene.pumpkins.find(x => x.id === hit.id)!, y = Math.min(renderer.centreOf(hit.id)![1], renderer.ray(e.clientX, e.clientY).o[1] - 0.3);
      const at = renderer.level(e.clientX, e.clientY, y);
      drag = { id: hit.id, y, off: at ? [p.at[0] - at[0], p.at[1] - at[1]] : [0, 0], from: [...p.at] as [number, number], x0: e.clientX, y0: e.clientY, moved: false, cx: e.clientX, cy: e.clientY };
      pop.hidden = true;
      return true;
    }
    return false;
  },
  move(e) {
    if (step === 'stencil' && sheet) { const at = sheetAt(e); if (at || !gourdDraw) sheet.drag(at, e.pointerType === 'pen' ? e.pressure : 0.5); return; }
    if (step === 'carve' && work && carving) {
      const at = sheetAt(e, true);
      if (!at) return;
      modelOf(work).carveMove(at[0], at[1]);
      return;
    }
    if (step === 'porch' && drag) {
      if (!drag.moved && Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < 5) return;
      drag.moved = true;
      drag.cx = e.clientX;
      drag.cy = e.clientY;
      dragTo();
    }
  },
  end(e) {
    if (step === 'stencil' && sheet) { gourdDraw = false; sheet.release(false); return; }
    if (step === 'carve' && work && carving) {
      carving = false;
      modelOf(work).carveUp();
      if (scrapeFrom && Math.hypot(e.clientX - scrapeFrom[0], e.clientY - scrapeFrom[1]) > 20) peel(e.clientX, e.clientY);
      scrapeFrom = null;
    }
    if (drag) {
      // a click rather than a drag: it rocks
      if (!drag.moved) { const hit = renderer.pick(e.clientX, e.clientY); if (hit) renderer.nudge(hit.id, hit.hit.p, v3.scale(hit.hit.n, -0.6)); }
      const moved = drag.moved;
      if (moved) settleDrop(drag.id, drag.from);
      drag = null;
      saveDraft();
      // a new one is moved close up, alone; once it is first let go the ring to turn and size it appears
      if (placing && moved) { placeWide = true; fitPlacing(); }
    }
  },
  cancel() {
    if (step === 'stencil' && sheet) { gourdDraw = false; sheet.release(true); return; }
    if (step === 'carve' && work && carving) { carving = false; modelOf(work).cancel(); modelOf(work).carveDraft = null; renderer.restencil(work.id); }
    if (drag?.moved) settleDrop(drag.id, drag.from);
    drag = null;
  },
  hover(e) {
    if (step === 'stencil' && sheet) { sheet.drag(sheetAt(e)); return; }
    if (step !== 'choose' || tweens.length) return;
    hovered = renderer.pick(e.clientX, e.clientY)?.id ?? '';
    canvas.style.cursor = hovered ? 'pointer' : '';
  },
};

orbit.onTap = e => {
  if (photo) return;
  const hit = renderer.pick(e.clientX, e.clientY);
  if (hit) renderer.nudge(hit.id, hit.hit.p, v3.scale(hit.hit.n, -0.6));
  if (step !== 'porch' || viewOnly) return;
  if (placing) return;
  // a tap away from an open menu or pop-up closes it
  closePorchBits();
};

// the ring: drag round it to turn the pumpkin
let ringDrag: { a0: number; t0: number; c: V3 } | null = null;
const angleAt = (e: PointerEvent, c: V3) => { const at = renderer.level(e.clientX, e.clientY, c[1]); return at ? Math.atan2(-(at[1] - c[2]), at[0] - c[0]) : null; };
for (const h of [ringHit, turner] as (HTMLElement | SVGPathElement)[]) h.addEventListener('pointerdown', (e: Event) => {
  const p = scene.pumpkins.find(x => x.id === sel), f = sel ? renderer.footOf(sel) : null, pe = e as PointerEvent;
  if (!p || !f) return;
  // a small gourd can sit wholly inside the ring's band: a press on the gourd itself moves it
  if (h === ringHit && renderer.pick(pe.clientX, pe.clientY)?.id === sel) { canvas.dispatchEvent(new PointerEvent('pointerdown', pe)); return; }
  const a = angleAt(pe, f.at);
  if (a === null) return;
  h.setPointerCapture((e as PointerEvent).pointerId);
  ringDrag = { a0: a, t0: p.turn, c: f.at };
  hidePop();
});
for (const h of [ringHit, turner] as (HTMLElement | SVGPathElement)[]) h.addEventListener('pointermove', (e: Event) => {
  const p = scene.pumpkins.find(x => x.id === sel);
  if (!ringDrag || !p) return;
  const a = angleAt(e as PointerEvent, ringDrag.c);
  if (a !== null) { p.turn = ringDrag.t0 + (a - ringDrag.a0); dirty = true; }
});
for (const h of [ringHit, turner] as (HTMLElement | SVGPathElement)[]) h.addEventListener('pointerup', () => { if (ringDrag) { ringDrag = null; saveDraft(); } });

// the knob: out from the middle to make it bigger, in for smaller; it only ever grows evenly
let knobDrag: { d0: number; s0: number; r0: number } | null = null;
knob.addEventListener('pointerdown', e => {
  const p = scene.pumpkins.find(x => x.id === sel), f = sel ? renderer.footOf(sel) : null;
  if (!p || !f) return;
  const at = renderer.level(e.clientX, e.clientY, f.at[1]);
  if (!at) return;
  knob.setPointerCapture(e.pointerId);
  knobDrag = { d0: Math.max(0.05, Math.hypot(at[0] - f.at[0], at[1] - f.at[2])), s0: p.scale ?? 1, r0: f.r };
});
knob.addEventListener('pointermove', e => {
  const p = scene.pumpkins.find(x => x.id === sel), f = sel ? renderer.footOf(sel) : null;
  if (!knobDrag || !p || !f) return;
  const at = renderer.level(e.clientX, e.clientY, f.at[1]);
  if (!at) return;
  scaleTo(p, knobDrag.s0 * Math.hypot(at[0] - f.at[0], at[1] - f.at[2]) / knobDrag.d0, knobDrag.r0 / knobDrag.s0);
});
knob.addEventListener('pointerup', () => { if (knobDrag) { knobDrag = null; saveDraft(); if (placing) fitPlacing(); } });

/**
 * Make a pumpkin this size. As it grows it shoulders off the rails and its neighbours; with no room left it stays as
 * it was. While it is being placed the view backs off to fit it once the sizing stops, and never closes in as it shrinks
 */
function scaleTo(p: PlacedPumpkin, scale: number, rPerScale = radiusOf(p) / (p.scale ?? 1)) {
  const want = +clamp(scale, MIN_SCALE, MAX_SCALE).toFixed(2);
  if (want === (p.scale ?? 1)) return;
  const r = rPerScale * want, others = discsAround(p.id);
  const to = settle(p.at, p.at, r, others, Renderer.onPorch);
  if (!clear(to, r, others)) return;
  p.at = to;
  p.scale = want;
  dirty = true;
  if (placing === p.id && !knobDrag) fitSoon();
}

/** a trackpad pinch over a pumpkin on the porch sizes it (Chrome and Firefox send it as a wheel with ctrl, Safari as a
 *  gesture); anywhere else it zooms the view */
const pinchable = (x: number, y: number) => {
  if (step !== 'porch' || viewOnly || photo || zoomed) return null;
  const hit = renderer.pick(x, y);
  return hit && (!placing || hit.id === placing) ? scene.pumpkins.find(q => q.id === hit.id) ?? null : null;
};
let wheelAt = 0, wheelP: PlacedPumpkin | null = null;
orbit.onWheel = e => {
  if (!e.ctrlKey) return false;
  // one pinch keeps to what it began on, even once the gourd has shrunk out from under the pointer
  if (performance.now() - wheelAt > 200) wheelP = pinchable(e.clientX, e.clientY);
  wheelAt = performance.now();
  const p = wheelP;
  if (!p) return false;
  if (!placing && sel !== p.id) selectPumpkin(p.id);
  scaleTo(p, (p.scale ?? 1) * Math.exp(-e.deltaY * 0.01));
  // a pinch held on past the size cap is still a pinch: the view waits for it to stop
  if (placing === p.id && !knobDrag) fitSoon();
  saveDraft();
  return true;
};
let gesture: { p: PlacedPumpkin; s0: number } | null = null;
type Gesture = Event & { scale: number; clientX: number; clientY: number };
canvas.addEventListener('gesturestart', e => {
  e.preventDefault();
  const g = e as Gesture, p = performance.now() - wheelAt > 200 ? pinchable(g.clientX, g.clientY) : null;
  gesture = p ? { p, s0: p.scale ?? 1 } : null;
  if (p && !placing && sel !== p.id) selectPumpkin(p.id);
});
canvas.addEventListener('gesturechange', e => {
  e.preventDefault();
  if (gesture && performance.now() - wheelAt > 200) scaleTo(gesture.p, gesture.s0 * (e as Gesture).scale);
});
canvas.addEventListener('gestureend', e => { e.preventDefault(); if (gesture) { gesture = null; saveDraft(); } });

/** the things that follow the scene round on screen: labels under the gourds, the ring, knob and menu, the pick's outline */
function placeMarks() {
  const r = canvas.getBoundingClientRect();
  // the sign hangs on the railing on a wide screen, and fades when a pumpkin is behind it
  const sign = $('add'), hang = step === 'porch' && !portrait();
  sign.classList.toggle('hung', hang);
  if (hang) {
    const [px, py] = renderer.project(SIGN_AT), w = sign.offsetWidth, h = sign.offsetHeight;
    const x = clamp(px, w / 2 + 12, r.width - w / 2 - 12), y = clamp(py - 10, 60, r.height - h - 110);
    sign.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, 0)`;
    const near = scene.pumpkins.some(p => {
      const c = renderer.screenOf(p.id);
      return c && c[0] > x - w / 2 - 30 && c[0] < x + w / 2 + 30 && c[1] > y - 20 && c[1] < y + h + 40;
    });
    sign.classList.toggle('shy', near);
  } else { sign.style.transform = ''; sign.classList.remove('shy'); }
  ringSvg.setAttribute('viewBox', `0 0 ${r.width} ${r.height}`);
  if (step === 'choose') {
    // just over the bottom of each gourd, at the front of its foot; on a tall phone the gourds are small, so the label
    // hangs underneath instead, and is then pushed clear of the words and buttons round them and of the other labels
    const tallScreen = portrait(), put: DOMRect[] = [];
    const avoid = ['.arch', '#pick-b', '.hh', '#rand-b'].map(q => document.querySelector(q)?.getBoundingClientRect()).filter((q): q is DOMRect => !!q && q.width > 0);
    const hit = (a: DOMRect, b: DOMRect) => a.left < b.right + 4 && a.right > b.left - 4 && a.top < b.bottom + 4 && a.bottom > b.top - 4;
    const bs = [...labels.querySelectorAll<HTMLElement>('.gourd-label:not(.peel)')];
    const at = bs.map(b => renderer.footOf(b.dataset.id!));
    // the lowest first: it has least room to move
    const order = bs.map((_, i) => i).sort((i, j) => (at[j]?.at[2] ?? 0) - (at[i]?.at[2] ?? 0));
    for (const i of order) {
      const b = bs[i], f = at[i];
      if (!f) continue;
      const [x0, y0] = renderer.project(v3.add(f.at, [0, f.r * (tallScreen ? 0 : 0.12), f.r * (tallScreen ? 0.7 : 1)]));
      // a long name on the outer gourd of a squarish screen would run off the edge
      const w = b.offsetWidth, h = b.offsetHeight, hw = w / 2 + 8, x = clamp(x0, hw, r.width - hw);
      // the sticker turns about its top left: tilted down its right end hangs below the box, tilted up it rises above
      const turn = Math.sin(parseFloat(b.querySelector<HTMLElement>('.sticker')?.style.getPropertyValue('--tilt') || '0') * Math.PI / 180) * w;
      const up = Math.max(0, -turn), down = Math.max(0, turn);
      let y = tallScreen ? y0 : y0 - h / 2;
      // a phone on its side has the gourds close together too: the labels never overlap each other
      for (let k = 0; k < 6; k++) {
        const box = new DOMRect(x - w / 2, y - up, w, h + up + down), o = [...avoid, ...put].find(q => hit(box, q));
        if (!o) break;
        // over the thing in the way if it is lower down, under it if it is higher
        y = o.top + o.height / 2 > y + h / 2 ? o.top - h - down - 6 : o.bottom + up + 6;
      }
      y = clamp(y, 8 + up, r.height - h - down - 8);
      put.push(new DOMRect(x - w / 2, y - up, w, h + up + down));
      b.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, 0)`;
      b.classList.toggle('hot', hovered === b.dataset.id);
    }
  }
  const p = step === 'porch' && !viewOnly && !spinning ? scene.pumpkins.find(x => x.id === sel) : undefined, f = p ? renderer.footOf(p.id) : null;
  const show = !!(p && f) && !(placing && !placeWide);
  ringSvg.classList.toggle('on', show);
  knob.hidden = turner.hidden = !show;
  menu.hidden = !show || !!placing || !!zoomed || !!drag?.moved || !!ringDrag || !!knobDrag;
  if (show) {
    const R = f!.r * 1.3, pts: [number, number][] = [];
    for (let i = 0; i <= 48; i++) {
      const a = (i / 48) * Math.PI * 2;
      pts.push(renderer.project(v3.add(f!.at, [Math.cos(a) * R, 0.012, Math.sin(a) * R])));
    }
    const d = 'M' + pts.map(q => q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join('L');
    ringPath.setAttribute('d', d);
    ringHit.setAttribute('d', d);
    const k = pts.reduce((a, b) => (b[0] > a[0] ? b : a)), front = pts.reduce((a, b) => (b[1] > a[1] ? b : a));
    // a small pumpkin seen from far off has a ring smaller than the handles: hold them clear of it (the knob scales by ratio,
    // so grabbing it further out still works)
    const c0 = renderer.project(f!.at), kx = Math.max(k[0], c0[0] + 46), fy = Math.max(front[1], c0[1] + 40);
    knob.style.transform = `translate(${kx.toFixed(1)}px, ${k[1].toFixed(1)}px)`;
    turner.style.transform = `translate(${front[0].toFixed(1)}px, ${fy.toFixed(1)}px)`;
    // kept on screen: on a phone a pumpkin near the edge would otherwise push Remove off it
    const top = renderer.project(f!.top), mw = menu.offsetWidth / 2 + 8, mh = menu.offsetHeight + 22;
    const mx = Math.min(Math.max(top[0], mw), innerWidth - mw), my = Math.max(top[1], mh);
    menu.style.transform = `translate(${mx.toFixed(1)}px, ${my.toFixed(1)}px)`;
  }
}

// ---- steps

function go(s: Step) {
  addLabel();
  if (viewOnly && s !== 'porch') return;
  step = s;
  document.body.dataset.step = s;
  $('back-b').lastElementChild!.textContent = editing && (s === 'carve' || s === 'stencil') ? 'Back to porch' : 'Back';
  renderer.stage = s === 'porch' ? null : { spots: [], lamp: 1 };
  renderer.lit = s === 'porch' || s === 'carve';
  if (s !== 'stencil') { sheet?.dispose(); sheet = null; }
  if (s !== 'choose') labels.innerHTML = '';
  if (s !== 'porch') { closePorchBits(); setPlacing(''); setPhoto(false); }
  if (s !== 'choose') $('picker').hidden = true;
  cardHost.innerHTML = '';
  cardOpen = false;
  buildCarveUI();
  dirty = true;
  renderer.sync(shown(), scene.pumpkins.map(p => p.id));
  frameStep();
}

// ---- sharing: a picture, a message, a link that always shows this version

/** the porch from the current angle, as close as it can get with every pumpkin in the picture */
function sharePicture(): string | undefined {
  const cs = scene.pumpkins.map(p => ({ c: renderer.centreOf(p.id), r: sizeOf(p) * 1.25 })).filter(x => x.c) as { c: V3; r: number }[];
  if (!cs.length || typeof renderer.snapshot !== 'function') return undefined;
  const mid = cs.reduce((a, x) => v3.add(a, v3.scale(x.c, 1 / cs.length)), [0, 0, 0] as V3);
  // the closest the camera can come along the view direction with every pumpkin still in the 1200x630 frame
  const yaw = orbit.goal.yaw, pitch = Math.max(0.16, orbit.goal.pitch), cp = Math.cos(pitch);
  const back: V3 = [Math.sin(yaw) * cp, Math.sin(pitch), Math.cos(yaw) * cp];
  const right = v3.norm(v3.cross([0, 1, 0], back)), up = v3.cross(back, right);
  // the renderer holds the vertical angle at fov over 1.2 for anything wider than 1.2:1
  const ty = Math.tan(FOV / 2) / 1.2, tx = ty * (1200 / 630);
  const target = v3.add(mid, [0, 0.04, 0]);
  let dist = 0.6;
  for (const { c, r } of cs) {
    const d = v3.sub(c, target), along = v3.dot(d, back);
    dist = Math.max(dist, along + (Math.abs(v3.dot(d, right)) + r) / tx, along + (Math.abs(v3.dot(d, up)) + r) / ty);
  }
  const cam = renderer.camera, keep = { ...cam, target: [...cam.target] as V3 };
  Object.assign(cam, { yaw, pitch, target, dist: dist * 1.08, lens: 0, lensX: 0, fov: FOV });
  try { return renderer.snapshot(1200, 630); } catch { return undefined; } finally { Object.assign(renderer.camera, keep); }
}

let shareImage: string | undefined;
let shareBase: HTMLImageElement | null = null;
const shareDlg = $('share-dlg') as HTMLDialogElement;

/** the 1200x630 picture: the porch with "Happy Halloween" big across the top, the message under it, and who it is from at the bottom */
function composeShare(): string | undefined {
  if (!shareBase) return undefined;
  const W = 1200, H = 630, c = document.createElement('canvas');
  c.width = W; c.height = H;
  const msg = ($('share-msg') as HTMLInputElement).value.trim(), name = ($('share-name') as HTMLInputElement).value.trim();
  drawCard(c.getContext('2d')!, shareBase, W, H, msg, name, getComputedStyle(document.body).fontFamily);
  return c.toDataURL('image/jpeg', 0.86);
}
function refreshShareImage() {
  shareImage = composeShare();
  const img = $('share-img') as HTMLImageElement;
  img.hidden = !shareImage;
  if (shareImage) img.src = shareImage;
}
$('share-name').addEventListener('input', refreshShareImage);
$('share-msg').addEventListener('input', refreshShareImage);

/** the share box, with the photo taken (or, without one, the porch framed round every pumpkin) */
async function openShare(taken?: string) {
  // the chosen pumpkin's ring and menu would sit on behind the box
  sel = '';
  hidePop();
  const g = orbit.goal;
  // the photo's tilt, Vertigo and filter go with it, so the link opens looking like the picture
  scene.camera = { yaw: g.yaw, pitch: g.pitch, dist: g.dist, target: [...g.target] as [number, number, number], ...(photo ? { roll: g.roll ?? 0, fov: g.fov ?? FOV } : {}) };
  scene.look = photo && look.filter ? { filter: look.filter } : undefined;
  const shot = taken ?? sharePicture();
  const nameEl = $('share-name') as HTMLInputElement;
  if (!nameEl.value) { try { nameEl.value = localStorage.getItem('pumpkin.name') ?? ''; } catch { /* private mode */ } }
  $('share-a').hidden = false;
  $('share-b').hidden = true;
  shareDlg.showModal();
  shareBase = null;
  if (shot) {
    try { await Promise.all([document.fonts.load('100px Creepster'), document.fonts.load('58px "Mystery Quest"')]); } catch { /* the fallback face will do */ }
    const im = new Image();
    im.src = shot;
    try { await im.decode(); shareBase = im; } catch { shareBase = null; }
  }
  refreshShareImage();
}
$('save').addEventListener('click', () => setPhoto(true));

// ---- photo mode: Share opens a viewfinder on the porch; turn and zoom as ever, tilt, widen, focus, a filter, then take it

let photo = false;
const look: PhotoLook = { filter: 0, focus: 3, blur: 0 };
const phIn = (id: string) => $(id) as HTMLInputElement;
const PH = { zoom: phIn('ph-zoom'), tilt: phIn('ph-tilt'), vertigo: phIn('ph-vertigo') };
const FOV_RANGE = [0.4, 1.3], ROLL = 0.35;
const toLog = (v: number, [a, b]: number[]) => a * Math.pow(b / a, v), fromLog = (x: number, [a, b]: number[]) => Math.log(x / a) / Math.log(b / a);
const filterBs = FILTERS.map((name, i) => {
  const b = button(name, () => { look.filter = i; filterBs.forEach((x, j) => { x.classList.toggle('on', j === i); x.setAttribute('aria-checked', String(j === i)); }); }, 'purp ph-fb' + (i ? '' : ' on'));
  b.setAttribute('role', 'radio');
  b.setAttribute('aria-checked', String(!i));
  b.dataset.f = String(i);
  $('ph-filters').append(b);
  return b;
});

function setPhoto(on: boolean) {
  if (on === photo || (on && (step !== 'porch' || placing))) return;
  photo = on;
  document.body.classList.toggle('photo-on', on);
  $('photo').hidden = !on;
  const g = orbit.goal;
  if (on) {
    closePorchBits();
      PH.tilt.value = String((g.roll ?? 0) / ROLL);
    PH.vertigo.value = String(fromLog(g.fov ?? FOV, FOV_RANGE));
    look.blur = 0;
    phTab('zoom');
    placeFinder();
  } else {
    renderer.photo = null;
    g.roll = 0;
    g.fov = FOV;
    g.lens = 0; g.lensX = 0;
    finderAt = '';
  }
}
/** the frame on screen, in CSS pixels: as big as fits between the top bar and the controls, at 1200:630 */
function finderRect() {
  const c = canvas.getBoundingClientRect(), bar = $('topbar').getBoundingClientRect(), ui = $('photo-ui').getBoundingClientRect();
  const top = Math.max(c.top, bar.bottom) + 10, bottom = Math.min(c.bottom, ui.top) - 10, avail = Math.max(40, bottom - top), ar = 1200 / 630;
  let w = c.width - 24, h = w / ar;
  if (h > avail) { h = avail; w = h * ar; }
  return { x: c.left + (c.width - w) / 2, y: top + (avail - h) / 2, w, h, c };
}
let finderAt = '';
function placeFinder() {
  const r = finderRect(), key = [r.x, r.y, r.w, r.h].map(Math.round).join(',');
  if (key === finderAt) return r;
  finderAt = key;
  Object.assign($('finder').style, { left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` });
  return r;
}
/** each frame in photo mode: the frame where it should be, the look on, the zoom slider following a wheel or a pinch */
function photoFrame() {
  const r = placeFinder(), c = r.c;
  // what the view turns round sits in the middle of the frame, not of the screen
  orbit.goal.lens = 1 - (r.y + r.h / 2 - c.top) / c.height * 2;
  orbit.goal.lensX = (r.x + r.w / 2 - c.left) / c.width * 2 - 1;
  renderer.photo = { ...look, blur: look.blur * r.h / r.c.height };
  if (document.activeElement !== PH.zoom) PH.zoom.value = String(1 - fromLog(orbit.goal.dist, [orbit.bounds.min, orbit.bounds.max]));
  const t = renderer.tier;
  if (document.body.dataset.tier !== t) document.body.dataset.tier = t;
}
PH.zoom.addEventListener('input', () => { const d = toLog(1 - +PH.zoom.value, [orbit.bounds.min, orbit.bounds.max]); if (!orbit.nudge(g => { g.dist = d; })) PH.zoom.value = String(1 - fromLog(orbit.goal.dist, [orbit.bounds.min, orbit.bounds.max])); });
PH.tilt.addEventListener('input', () => { const r = +PH.tilt.value * ROLL; if (!orbit.nudge(g => { g.roll = r; })) PH.tilt.value = String((orbit.goal.roll ?? 0) / ROLL); });
// Vertigo: the dolly zoom. The angle widens as the camera closes in, so what it turns round keeps its size and the
// porch behind stretches away; held to the porch limits by taking the angle only as far as the distance can follow
PH.vertigo.addEventListener('input', () => {
  const g = orbit.goal, f0 = g.fov ?? FOV, k = g.dist * Math.tan(f0 / 2);
  const d = clamp(k / Math.tan(toLog(+PH.vertigo.value, FOV_RANGE) / 2), orbit.bounds.min, orbit.bounds.max), f = 2 * Math.atan(k / d);
  if (!orbit.nudge(g => { g.fov = f; g.dist = d; })) PH.vertigo.value = String(fromLog(f0, FOV_RANGE));
  else if (Math.abs(fromLog(f, FOV_RANGE) - +PH.vertigo.value) > 0.01) PH.vertigo.value = String(fromLog(f, FOV_RANGE));
});
/** one control at a time: the tab picks which slider, or the filters, sits above the row */
function phTab(c: string) {
  document.querySelectorAll<HTMLElement>('#photo-ui .ph-tab').forEach(b => { const on = b.dataset.c === c; b.classList.toggle('on', on); b.setAttribute('aria-selected', String(on)); });
  document.querySelectorAll<HTMLElement>('#photo-ui .ph-panel > [data-c]').forEach(e => { e.hidden = e.dataset.c !== c; });
}
document.querySelectorAll<HTMLElement>('#photo-ui .ph-tab').forEach(b => b.addEventListener('click', () => phTab(b.dataset.c!)));
/** the frame as a 1200x630 picture: the same camera, its angle narrowed and its lens shifted to the frame */
function photoShot(): string | undefined {
  const r = finderRect(), c = r.c, cam = renderer.camera, keep = { ...cam, target: [...cam.target] as V3 };
  const k = r.h / c.height, t = Math.tan(renderer.fovY / 2);
  const cx = (r.x + r.w / 2 - c.left) / c.width * 2 - 1, cy = 1 - (r.y + r.h / 2 - c.top) / c.height * 2;
  Object.assign(cam, { fov: 2 * Math.atan(1.2 * k * t), lens: ((cam.lens ?? 0) - cy) / k, lensX: ((cam.lensX ?? 0) - cx) * c.width / r.w });
  renderer.photo = { ...look };
  try { return renderer.snapshot(1200, 630); } catch { return undefined; } finally { Object.assign(renderer.camera, keep); }
}
$('ph-take').addEventListener('click', () => {
  const shot = photoShot();
  const f = el('div', 'flash');
  document.body.append(f);
  setTimeout(() => f.remove(), 600);
  void openShare(shot);
});
gooDrips($('ph-take'));
addEventListener('keydown', e => { if (e.key === 'Escape' && photo && !document.querySelector('dialog[open]')) setPhoto(false); });

$('share-go').addEventListener('click', async () => {
  const b = $('share-go') as HTMLButtonElement, message = ($('share-msg') as HTMLInputElement).value.trim(), name = ($('share-name') as HTMLInputElement).value.trim();
  b.disabled = true;
  try {
    try { localStorage.setItem('pumpkin.name', name); } catch { /* private mode */ }
    refreshShareImage();
    // the link is there at once; the server draws its picture in the next few seconds, and a link pasted before that
    // previews with the front page's picture (pumpkin#90)
    const r = await api.share(scene, message, name);
    sceneId = r.id;
    saveDraft();
    const url = r.url, u = encodeURIComponent(url), t = encodeURIComponent(message || (name ? `Happy Halloween from ${name}` : 'Happy Halloween'));
    ($('share-url') as HTMLInputElement).value = url;
    ($('sh-fb') as HTMLAnchorElement).href = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    ($('sh-x') as HTMLAnchorElement).href = `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    ($('sh-li') as HTMLAnchorElement).href = `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    $('share-a').hidden = true;
    $('share-b').hidden = false;
  } catch (e) {
    toast(`Couldn't share it: ${(e as Error).message}`);
  } finally { b.disabled = false; }
});
$('share-copy').addEventListener('click', async () => {
  const url = ($('share-url') as HTMLInputElement).value;
  try { await navigator.clipboard.writeText(url); toast('Link copied.'); } catch {
    // no clipboard API off https (a phone on the LAN): select it and try the old way
    ($('share-url') as HTMLInputElement).select();
    if (document.execCommand('copy')) toast('Link copied.');
  }
});

const escape = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

// Make your own: off the shared porch and on to your own (the one carried on with in this browser, or a new one), at
// the gourds
$('own').addEventListener('click', () => {
  viewOnly = false;
  intro = null;
  renderer.photo = null;
  document.body.classList.remove('viewing');
  history.replaceState(null, '', '/');
  scene = defaultScene();
  sceneId = null;
  try { const d = JSON.parse(localStorage.getItem('pumpkin.draft') ?? 'null'); const s = normaliseScene(d?.scene); if (s) { scene = s; sceneId = d?.id ?? null; } } catch { /* a fresh porch */ }
  delete scene.look;
  startChoose();
});

// ---- the HUD: which engine, how fast

const hud = $('hud');
hud.hidden = !LOCAL;
function refreshHud() {
  if (LOCAL) hud.textContent = `${TIER_LABEL[renderer.tier]}${tierPref === 'auto' ? '' : '*'} · ${gov.fps ? Math.round(gov.fps) : '–'} fps`;
  const q = TIER_LABEL[renderer.tier], ql = $('quality-l');
  if (ql.textContent !== q) ql.textContent = q;
}

// ---- the loop

let last = performance.now(), hudAt = 0;
// ?burn=40 stands in for a weak GPU: that many ms a frame on Full, about half on Lit, a fifth on Plain, so the
// governor's step down can be watched on a fast machine
const burn = Math.min(200, Math.max(0, Number(Q.get('burn')) || 0)), burnShare: Record<Tier, number> = { ultra: 1, high: 0.75, mid: 0.55, low: 0.2 };
function loop(now: number) {
  requestAnimationFrame(loop);
  const ms = now - last, dt = Math.min(0.1, ms / 1000);
  last = now;
  runTweens(now);
  if (step === 'choose' && !tweens.length) {
    // each gourd turns toward you when you point at it, and sways a touch when you don't
    candidates.forEach((c, i) => {
      const goal = hovered === c.id ? 0.7 : (i - 1) * -0.25 + Math.sin(now / 1400 + i * 2) * 0.06;
      c.turn += (goal - c.turn) * (1 - Math.exp(-dt * 4));
    });
    dirty = true;
  }
  if (spinning) {
    const p = scene.pumpkins.find(x => x.id === spinning!.id);
    if (p) p.turn = spinning.turn + (now - spinning.t0) / 1000 * 0.7;
    dirty = true;
  }
  if (dirty) {
    const s = shown();
    renderer.sync(s, scene.pumpkins.map(p => p.id));
    if (renderer.stage) renderer.stage.spots = s.pumpkins.map(p => [p.at[0], p.at[1], sizeOf(p) * 2.6] as [number, number, number]);
    dirty = false;
  }
  if (intro) {
    const k = Math.min(1, (now - intro.t0) / intro.dur), e = ease(k), g = orbit.goal, a = intro.from, b = intro.to;
    g.dist = lerp(a.dist, b.dist, e);
    g.yaw = lerp(a.yaw, b.yaw, e);
    g.pitch = lerp(a.pitch, b.pitch, e);
    if (k >= 1) intro = null;
  }
  if (photo) photoFrame();
  orbit.step(dt);
  // on a phone the paper has the screen to itself: the gourd waits out of sight until it flies in to be carved
  const stow = step === 'stencil' && work && portrait() ? work.id : '';
  if (stow !== stowed) { if (stowed) renderer.hidden.delete(stowed); if (stow) renderer.hidden.add(stow); stowed = stow; }
  // the stencil on the skin: its ink while drawing and deciding, only its dashed outline on the tools with Show stencil;
  // the porch shows only what is cut. None while the paper flies on and wraps round: the trace is what it leaves behind
  const wrapping = flying && (!renderer.paper || renderer.paper.wrap < 1);
  renderer.trace = !wrapping && (step === 'stencil' || (step === 'carve' && (carveMode === 'ask' || carveMode === 'review')));
  renderer.dots = !wrapping && step === 'carve' && carveMode === 'tools' && showStencil && work ? work.id : '';
  renderer.frame(now / 1000);
  if (!document.body.classList.contains('drawn')) document.body.classList.add('drawn');
  placeMarks();
  if (step === 'carve' || step === 'stencil' || selKey) drawSel();
  if (burn) { const until = performance.now() + burn * burnShare[renderer.tier]; while (performance.now() < until); }
  gov.feed(ms, now, tierPref === 'auto' && renderer.tier !== 'low');
  if (now - hudAt > 500) { refreshHud(); hudAt = now; }
}

// ---- start

/** the version id in a share link: /s/<id>, /s/?<id>, or ?s=<id> from before versions */
function linkId(): { id: string; old: boolean } | null {
  const m = location.pathname.match(/^\/s\/([0-9A-Za-z]{10})/) ?? (location.pathname.startsWith('/s') ? location.search.match(/^\?([0-9A-Za-z]{10})/) : null);
  if (m) return { id: m[1], old: false };
  const s = Q.get('s');
  return s ? { id: s, old: true } : null;
}

/** a stranger's first sight of a shared porch: from far out, easing in over a few seconds, until they touch it */
let intro: { t0: number; dur: number; from: { dist: number; yaw: number; pitch: number }; to: { dist: number; yaw: number; pitch: number } } | null = null;
function startIntro() {
  const g = orbit.goal, to = { dist: g.dist, yaw: g.yaw, pitch: g.pitch };
  const far = { dist: to.dist * 2.1 + 2, yaw: to.yaw + 0.4, pitch: to.pitch + 0.3 }, from = { ...to };
  // start as far out along that line as the porch view goes
  const c = { ...g, ...far };
  porchView(c);
  Object.assign(from, { dist: c.dist, yaw: c.yaw, pitch: c.pitch });
  Object.assign(g, from);
  Object.assign(renderer.camera, from);
  intro = { t0: performance.now(), dur: 5200, from, to };
  const stop = () => { intro = null; };
  for (const ev of ['pointerdown', 'wheel', 'keydown']) (ev === 'keydown' ? window : canvas).addEventListener(ev, stop, { once: true, capture: true });
}

/** `fn` once `n` more frames have been drawn */
function afterFrames(n: number, fn: () => void) {
  requestAnimationFrame(() => (n > 1 ? afterFrames(n - 1, fn) : fn()));
}

async function start() {
  // edit links carry the token in the fragment; links mailed before #85 had it in the query
  const H = new URLSearchParams(location.hash.slice(1));
  const link = linkId(), editPorch = H.get('edit') ?? Q.get('edit'), token = H.get('t') ?? Q.get('t');
  // the loading card the page opened behind (index.html) stays up while the gourds or a shared porch are built and the
  // first frames drawn, then fades: the first load blocks for a moment, and the half-made page underneath read as jerky
  document.body.classList.add('holding');
  let porchFirst = Q.get('step') === 'porch';
  const open = (s: Scene | null, editable: boolean, message: string, name = '') => {
    if (!s) throw new Error('that porch is damaged');
    scene = s;
    viewOnly = !editable;
    porchFirst = true;
    if (viewOnly) {
      document.body.classList.add('viewing');
      $('view-msg').textContent = message;
      $('view-from').textContent = name ? `from ${name}` : '';
      renderer.photo = { filter: s.look?.filter ?? 0, focus: 3, blur: 0 };
    } else {
      // your own porch comes back untilted and unfiltered
      if (s.camera) { delete s.camera.roll; delete s.camera.fov; }
      delete s.look;
    }
  };
  if (editPorch && token) {
    // back from the emailed link: this browser may edit the porch now, from its latest version
    try {
      const m = await api.redeem(editPorch, token);
      const r = await api.latest(m);
      open(normaliseScene(cleanScene(r.scene)), true, '');
      sceneId = r.id;
      toast('You can edit your porch again. Share it for a new link.');
    } catch (e) { toast(`That link didn't work: ${(e as Error).message}`); }
    history.replaceState(null, '', '/');
  } else if (link) {
    try {
      if (link.old) {
        const r = await api.loadOld(link.id);
        open(normaliseScene(r.scene), false, r.title || '');
      } else {
        const r = await api.loadVersion(link.id);
        sceneId = r.id;
        // a share is public before the worker has cleaned it: clean it here too
        open(normaliseScene(cleanScene(r.scene)), r.mine, r.message ?? '', r.name ?? '');
      }
    } catch (e) {
      toast(`Couldn't open that link: ${(e as Error).message}`);
    }
  } else {
    try { const d = JSON.parse(localStorage.getItem('pumpkin.draft') ?? 'null'); const s = normaliseScene(d?.scene); if (s) { scene = s; sceneId = d?.id ?? null; } } catch { /* a fresh porch */ }
  }
  if (porchFirst) {
    go('porch');
    // the turn and distance saved with it; the tilt and Vertigo only on someone else's link (frameStep)
    if (scene.camera) { const c = scene.camera; Object.assign(orbit.goal, { yaw: c.yaw, pitch: c.pitch, dist: c.dist, target: [...c.target] }); porchView(orbit.goal); }
  } else startChoose();
  Object.assign(renderer.camera, { ...orbit.goal, target: [...orbit.goal.target] });
  if (viewOnly && link && porchFirst) startIntro();
  gov.reset(performance.now());
  requestAnimationFrame(loop);
  document.fonts.ready.then(() => afterFrames(3, () => {
    document.body.classList.remove('loading');
    document.body.classList.add('landing');
    setTimeout(() => document.body.classList.remove('holding', 'landing'), 600);
  }));
  paintIcons();
  noteUndo();
  addEventListener('resize', () => { if (!orbit.busy && !tweens.length && !intro) frameStep(); });
}

start();

// for tests and for poking at it from the console
Object.assign(window, {
  __pumpkin: {
    renderer, gov, device, guess, orbit,
    get scene() { return scene; }, get step() { return step; }, get sel() { return sel; }, get work() { return work; },
    get candidates() { return candidates; }, get sheet() { return sheet; }, get busy() { return tweens.length > 0 || flying; },
    model: (id: string) => models.get(id), go, choose, startChoose, toCarve, done, edit, copy, selectPumpkin, setTierPref, addPumpkin, toPorch,
    setHand: (h: Hand, d?: number, c?: boolean) => { setHandRaw(h); if (d !== undefined) { if (h === 'fill') fillDepth = d; else depth = d; } if (c !== undefined) showStencil = c; carveMode = 'tools'; buildCarveUI(); },
    get carveMode() { return carveMode; }, get hand() { return hand; }, get placing() { return placing; }, get placeWide() { return placeWide; }, get intro() { return intro; }, openEdit, placed, setPlacing, frameStep, askRemove, removePumpkin, nextLight, openShare, sharePicture, setPhoto, photoShot, phTab, look, get photo() { return photo; },
    openScene, freeSpot, radiusOf, changed, fitPlacing, importSvg: (t: string) => sheet?.importSvg(t),
  },
});

// a second visit paints from the cache; not on local or LAN hosts, where the files change under you (?sw tests it there).
// A release made with bin/fresh has a new worker, which empties the old cache and takes over the page: a visitor who
// hasn't touched anything yet reloads into the new version there and then, anyone mid-carve keeps going and gets it next load.
if ('serviceWorker' in navigator && location.protocol === 'https:' && (!LOCAL || new URLSearchParams(location.search).has('sw'))) {
  const sw = navigator.serviceWorker, had = !!sw.controller;
  let touched = false;
  for (const ev of ['pointerdown', 'keydown']) addEventListener(ev, () => { touched = true; }, { capture: true, once: true });
  sw.addEventListener('controllerchange', () => {
    const last = Number(sessionStorage.getItem('pumpkin.fresh') ?? 0);
    if (!had || touched || Date.now() - last < 10000) return;
    sessionStorage.setItem('pumpkin.fresh', String(Date.now()));
    location.reload();
  });
  addEventListener('load', () => sw.register('sw.js').then(r => r.update()).catch(() => {}));
}
